/**
 * Feb 2026 · Purchase Orders — SO-style grid.
 *
 * Layout mirrors Sales Orders: one <tr> per PO line, first-line-of-group
 * carries PO No / Date / Supplier / Expected (rowSpan-ish via border), plus
 * search, overdue highlight, per-line actions and a "New Purchase Order"
 * dialog with a repeatable line grid.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import AllocationBadge from "@/components/AllocationBadge";
import { RowActions } from "@/components/RowActions";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { getUser, getToken } from "@/lib/secureStorage";
import { canSubmitDoc } from "@/lib/submitPerms";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  PlusIcon, TrashIcon, ClipboardTextIcon, ArrowsClockwiseIcon,
  MagnifyingGlassIcon, CheckCircleIcon, DownloadSimpleIcon,
  PencilSimpleIcon, PrinterIcon, ArrowCounterClockwiseIcon, XIcon,
} from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";
import { useLatestRowFlash } from "@/lib/latestRow";
import ModifyDocumentPopover from "@/components/ModifyDocumentPopover";
import CancelWithReasonDialog from "@/components/CancelWithReasonDialog";
import PoStatusReport from "@/pages/PoStatusReport";
import { fmtDateIST } from "@/lib/datetime";
import SearchablePicker from "@/components/SearchablePicker";
// Feb 2026 · Shared print entry point — PI / SO / PO all route their
// browser-print action through this so the visual template stays in
// perfect sync across the three documents.
import { printDocument as printDocumentHtml } from "@/components/DocumentPrintTemplate";
import {
  useGstPreview, GstControlBadge, GstRateCell, DiscountCells, GstResultPanel, GstTermsSection,
  GstHeaderFields, ChargesEditor,
} from "@/components/po/GstTerms";
import {
  createLineGst, editLineGst, numOrNull, gstHeaderPayload, gstHeaderFromPo, gstHeaderDiff,
  isGstControlled, savedResult, previewResult,
} from "@/lib/poGst";
import { canSubmitPo, isRejectedDraft, submitConfirmText, submitBlockedByGrn, GRN_SUBMIT_BLOCKED } from "@/lib/poLifecycle";
import { buildAmendBody, initAmendGst } from "@/lib/poAmend";
import { AmendGstPanels, CancelAmendmentDialog, AmendmentHistoryGst } from "@/components/po/AmendmentGst";

const inpCls = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const cellCls = "w-full h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]";
const lblCls = "text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block";

// Mar 3, 2026 · `expected_date` at line level (per SO/ESO parity) — falls
// back to the header expected_date when a line leaves it blank.
const GRN_LOCK_TEXT = "Commercial terms locked — GRN has been posted against this PO.";
const emptyLine = () => ({
  raw_material_id: "", qty_ordered: "", unit_cost: "",
  price_source: "", price_valid_to: "", remarks: "", expected_date: "",
  hsn_code: "", tax_pct: "",
  // PO-GST-01 · P2-D1-C · discounts + explicit confirmed-0% flag (blank ≠ confirmed 0).
  discount_pct: "", discount_amt: "", zero_confirmed: false,
  // Phase H1-η · D-η3 · Required-vs-Actual duality on PR-backed lines.
  // Empty when the header is DIRECT.
  required_rm_id: "",
  required_rm_code: "",
  source_pr_id: "",
  source_pr_line_no: 0,
  allocation_type: "",   // "EXACT" | "APPROVED_ALTERNATE" | "DIRECT_PURCHASE"
  alternate_approval_id: "",
  alternate_approval_no: "",
  alternate_priority: 0,
  // per-line eligible alt list, fetched from /rm-alternate-approvals/valid-for
  _validAlts: [],
});
const emptyHeader = () => ({
  supplier_id: "", po_date: new Date().toISOString().slice(0, 10),
  bill_from_address_id: "", ship_from_address_id: "",
  expected_date: "", remarks: "", lines: [emptyLine()],
  terms_and_conditions: "", tnc_template_id: "",
  procurement_source: "DIRECT",
  // PO-GST-01 · P2-D1-C · GST header inputs (computed by the backend only).
  place_of_supply_state_code: "", company_is_receiving_location: false, reverse_charge: false, charges: [],
  // Phase H1-η · additive procurement genealogy on the PO header when
  // PR-backed via the New PO dialog's Source PR picker (see below).
  source_pr_id: "", source_pr_no: "", source_mrp_no: "",
  source_work_order_id: "", source_work_order_no: "",
  source_sales_order_id: "", source_sales_order_no: "",
});

const badgeCls = (s) => ({
  "Received": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Partially Received": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Open": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  "Approved": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Authorized": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Draft": "bg-amber-500/15 text-amber-300 border-amber-500/40",
  "Pending Admin Approval": "bg-amber-500/15 text-amber-300 border-amber-500/40",
  "Cancelled": "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
}[s] || "bg-zinc-500/15 text-zinc-400 border-zinc-500/30");

// PO-AUTH-EDIT-AMEND-H01 · Phase 2A · Feb 2026 ─────────────────────
// Authorisation dimension (independent from fulfilment `status`).
// Maps the raw backend `approval_status` + `status` combination to a
// user-facing chip. `status` is fulfilment (Open/Partial/Received/Cancelled)
// and MUST NOT be conflated with authorisation — both dimensions are
// rendered as separate chips per owner Decision 1.
//   approval_status="Approved"|"Authorized"|"Authorised" → AUTHORISED
//   approval_status="Rejected"                            → REJECTED
//   status="Pending Admin Approval"                       → PENDING AUTHORISATION
//   otherwise                                             → NOT SUBMITTED
const authState = (p) => {
  const a = (p?.approval_status || "").trim();
  if (["Approved", "Authorized", "Authorised"].includes(a)) return "AUTHORISED";
  if (a === "Rejected") return "REJECTED";
  if (a === "Pending Approval" || (p?.status || "").trim() === "Pending Admin Approval") return "PENDING AUTHORISATION";
  return "DRAFT";
};
const authBadgeCls = (label) => ({
  "AUTHORISED": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "REJECTED": "bg-rose-500/15 text-rose-300 border-rose-500/40",
  "PENDING AUTHORISATION": "bg-amber-500/15 text-amber-300 border-amber-500/40",
  "DRAFT": "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
}[label] || "bg-zinc-500/15 text-zinc-400 border-zinc-500/30");
// True when the PO has passed Management Authorisation. Every buyer-side
// mutation control (Modify Order · Edit Adjustments) MUST be hidden when
// this returns true; the backend independently rejects via a fail-closed
// guard in `edit_po`.
const isAuthorisedPo = (p) => ["Approved", "Authorized", "Authorised"].includes((p?.approval_status || "").trim());

// PO-AUTH-EDIT-AMEND-H01 · Phase 2B.2 · Feb 2026 ─────────────────
// Third contextual amendment lifecycle indicator. Presented ONLY when
// an active amendment exists. Never collapses Fulfilment + Authorisation
// into one status (owner directive O).
//   active_amendment_ref present + latest entry state="Draft"
//        → { label:"AMD-001 · DRAFT", tone:"amber" }
//   active_amendment_ref present + entry state="Pending Re-authorisation"
//        → { label:"AMD-001 · PENDING RE-AUTHORISATION", tone:"cyan" }
//   revision > 0 (no active amendment)
//        → { label:"Revision N", tone:"emerald" }  (compact PO chip)
const activeAmendment = (p) => {
  const ref = (p?.active_amendment_ref || "").trim();
  const amendments = Array.isArray(p?.amendments) ? p.amendments : [];
  if (!ref) return null;
  return amendments.find(a => (a?.amendment_no || "") === ref) || null;
};
const amendState = (p) => {
  const amd = activeAmendment(p);
  if (amd && amd.state === "Draft") {
    // PO-2B2-UAT-D2 · Feb 2026 — Draft after Management Reject is
    // canonically distinct from a fresh Draft. Stored evidence
    // (`rejected_at` on the amendment entry) drives the tri-modal
    // label. NO backend state change — approval_status remains
    // "Approved" and the amendment state remains "Draft" per Style-Y.
    if (amd.rejected_at) {
      return { label: `${amd.amendment_no} · DRAFT — REJECTED, CORRECT & RESUBMIT`, cls: "bg-rose-500/15 text-rose-200 border-rose-500/40", state: "Draft-Rejected" };
    }
    return { label: `${amd.amendment_no} · DRAFT`, cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", state: "Draft" };
  }
  if (amd && amd.state === "Pending Re-authorisation") {
    return { label: `${amd.amendment_no} · PENDING RE-AUTHORISATION`, cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40", state: "Pending Re-authorisation" };
  }
  return null;
};
const revisionLabel = (p) => {
  const rev = Number(p?.revision || 0);
  return rev > 0 ? `Revision ${rev}` : "";
};

// DP-PO-AMEND-STATUS-DISPLAY · Feb 2026 — Owner-authorised UI-only
// clarification (§7.2 disposition (a)). While an Authorised PO has an
// Amendment in state "Pending Re-authorisation", the base PO stays
// AUTHORISED (sealed Style-Y invariant) but the Amendment-Pending signal
// MUST be co-located with the auth/lifecycle chips in the Status column
// and Detail dialog header. Draft / Draft-Rejected amendments do NOT
// carry this pill (Owner clause 6: "must NOT say Pending Management
// Authorisation before Submit"). This is a pure presentation helper —
// no backend/state change.
const amendPendingLabel = (p) => {
  const amd = activeAmendment(p);
  return (amd && amd.state === "Pending Re-authorisation") ? "AMENDMENT · PENDING MANAGEMENT AUTHORISATION" : "";
};

// Feb 2026 · Authorization Gate — PRs / POs in Draft or Pending Admin
// Approval are hidden from GRN + Material Issue pickers until an
// Administrator authorises them. Wherever a status chip is shown we
// also stamp a "Waiting for Auth" pill so buyers instantly see why a
// document isn't visible downstream.
const isWaitingAuth = (s) => ["Draft", "Pending Admin Approval", "OPEN", "PENDING_APPROVAL"].includes(s);

const fmtQty = (v) => (Number(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export default function PurchaseOrders() {
  const [pos, setPos] = useState([]);
  // P2 · Feb 2026 · Latest-row flash — fresh PO ids glow emerald for 3s.
  const flashingPoIds = useLatestRowFlash(pos, "id");
  // Mar 3, 2026 · Sub-tabs — Orders (existing grid) + Status Report
  // (new, mirrors SO Status Report). Preserved in localStorage so
  // deep-links back to the last-viewed sub-tab.
  const [subTab, setSubTab] = useState(() => {
    try { return localStorage.getItem("mes.po.subtab") || "orders"; } catch (_e) { return "orders"; }
  });
  const setSubTabPersist = (k) => { setSubTab(k); try { localStorage.setItem("mes.po.subtab", k); } catch (_e) { /* ignore */ } };
  const [cancelTarget, setCancelTarget] = useState(null); // {id, po_no}
  const [rms, setRms] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [poSearch, setPoSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  // Feb 2026 · Dynamic Order Modification — shared popover across SO/ESO/
  // PO/WO. Replaces the legacy inline Unapprove flow.
  const [modifyOpen, setModifyOpen] = useState(false);
  const [form, setForm] = useState(emptyHeader());
  const [saving, setSaving] = useState(false);
  // Feb 2026 · Purchase-side T&C templates. Loaded once on mount and
  // filtered server-side to template_type=purchase so sales T&Cs never
  // leak into a supplier PO.
  const [tncTemplates, setTncTemplates] = useState([]);

  const load = async () => {
    try {
      const [{ data: p }, { data: r }, { data: s }] = await Promise.all([
        api.get("/purchase-orders/"),
        api.get("/raw-materials/"),
        api.get("/suppliers/").catch(() => ({ data: [] })),
      ]);
      setPos(p || []);
      setRms((r || []).filter(x => x.is_active !== false));
      setSuppliers((s || []).filter(x => x.is_active !== false));
    } catch (_e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  // Feb 2026 · Load purchase T&C templates on mount. Dialog open reuses
  // this cache to pre-fill the default purchase template into the form.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tnc-templates/?template_type=purchase");
        setTncTemplates(data || []);
      } catch { /* silent — dialog still works without templates */ }
    })();
  }, []);

  const openNew = () => {
    const base = emptyHeader();
    const def = tncTemplates.find(t => t.is_default);
    if (def) { base.terms_and_conditions = def.text; base.tnc_template_id = def.id; }
    setForm(base);
    setDialogOpen(true);
  };
  const updLine = (idx, patch) => setForm(f => ({ ...f, lines: f.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) }));

  // Phase H1-η · D-η3/D-η4 · PR-backed workflow state.
  const [srcPrSearch, setSrcPrSearch] = useState("");
  const [openPrs, setOpenPrs] = useState([]);
  const [srcPr, setSrcPr] = useState(null);
  const [validAltsCache, setValidAltsCache] = useState({});

  useEffect(() => {
    if (!dialogOpen || form.procurement_source !== "PR") return;
    api.get("/prs/", { params: { status_in: "APPROVED,OPEN,PENDING_APPROVAL" } })
      .then(({ data }) => setOpenPrs(Array.isArray(data) ? data : []))
      .catch(() => setOpenPrs([]));
  }, [dialogOpen, form.procurement_source]);

  const _loadValidAlts = async (productId, requiredRmId) => {
    if (!productId || !requiredRmId) return [];
    const key = `${productId}|${requiredRmId}`;
    if (validAltsCache[key]) return validAltsCache[key];
    try {
      const { data } = await api.get("/rm-alternate-approvals/valid-for", {
        params: { product_id: productId, source_rm_id: requiredRmId },
      });
      const alts = data?.valid_alternates || [];
      setValidAltsCache(c => ({ ...c, [key]: alts }));
      return alts;
    } catch {
      return [];
    }
  };

  const pickSourcePr = async (prId) => {
    const pr = openPrs.find(p => p.id === prId);
    if (!pr) { setSrcPr(null); return; }
    setSrcPr(pr);
    // Populate supplier + lines[] from the PR. RM picker on each line
    // will be restricted to `Required RM ∪ valid-for(...)`.
    const productId = pr.article_id || pr.product_id || "";
    const nextLines = await Promise.all((pr.lines || []).map(async (ln, i) => {
      const reqId = ln.required_rm_id || ln.rm_id || "";
      const reqCode = ln.required_rm_code || ln.rm_code || "";
      const alts = await _loadValidAlts(productId, reqId);
      return {
        ...emptyLine(),
        line_no: i + 1,
        raw_material_id: reqId,   // default to EXACT
        required_rm_id: reqId,
        required_rm_code: reqCode,
        source_pr_id: pr.id,
        source_pr_line_no: Number(ln.line_no || (i + 1)),
        allocation_type: "EXACT",
        qty_required: String(ln.qty_total || ln.qty_required || ln.qty || 0),
        qty_adjustment: "0",
        uom: ln.uom || "kg",
        _validAlts: alts,
        _productId: productId,
      };
    }));
    setForm(f => ({
      ...f,
      supplier_id: pr.supplier_id || f.supplier_id,
      source_pr_id: pr.id,
      source_pr_no: pr.pr_no || "",
      source_mrp_no: pr.mrp_no || "",
      source_work_order_id: pr.work_order_id || "",
      source_work_order_no: pr.work_order_no || "",
      source_sales_order_id: pr.sales_order_id || "",
      source_sales_order_no: pr.sales_order_no || "",
      lines: nextLines.length > 0 ? nextLines : [emptyLine()],
    }));
  };

  const setPrBackedLineActualRm = async (idx, actualRmId) => {
    // On a PR-backed line the "Actual RM" is chosen from Required ∪
    // valid-for options. Update the classification snapshot inline; the
    // backend re-runs the authoritative validator on save.
    const ln = form.lines[idx];
    const isExact = actualRmId === ln.required_rm_id;
    const alt = (ln._validAlts || []).find(a => a.alternate_rm_id === actualRmId);
    updLine(idx, {
      raw_material_id: actualRmId,
      allocation_type: isExact ? "EXACT" : "APPROVED_ALTERNATE",
      alternate_approval_id: isExact ? "" : (alt?.rma_id || ""),
      alternate_approval_no: isExact ? "" : (alt?.rma_no || ""),
      alternate_priority:    isExact ? 0  : Number(alt?.priority || 0),
    });
    // Re-fetch the commercial rate against the new (Actual RM · Actual Supplier).
    priceLookup(idx, actualRmId);
  };
  const priceLookup = async (idx, rmId) => {
    const supplierId = form.supplier_id;
    if (!supplierId || !rmId) {
      updLine(idx, { raw_material_id: rmId, price_source: "", price_valid_to: "" });
      return;
    }
    // Feb 2026 · GST auto-fill · pull HSN + default_tax_rate from the
    // selected RM master row so buyers never key HSN/GST% by hand.
    // Existing overrides on the line are preserved.
    const rm = rms.find(r => r.id === rmId) || {};
    const gstAutoFill = {
      ...(rm.hsn_code ? { hsn_code: rm.hsn_code } : {}),
      ...(rm.default_tax_rate ? { tax_pct: String(Number(rm.default_tax_rate)), gst_rate_source: "", zero_confirmed: false } : {}),
    };
    try {
      const { data } = await api.get("/supplier-price-list/lookup", {
        params: { supplier_id: supplierId, rm_id: rmId, on_date: form.po_date || undefined },
      });
      if (data?.found) {
        const finalRate = data.landed_rate_inr ?? data.rate_inr ?? data.rate;
        const fx = Number(data.fx_to_inr || 1);
        const addons = Number(data.freight_pct || 0) + Number(data.gst_pct || 0) + Number(data.customs_pct || 0);
        const badgeBits = [];
        if (data.currency && data.currency !== "INR") badgeBits.push(`${data.currency} @ ${fx}`);
        if (addons > 0) badgeBits.push(`+${addons.toFixed(1)}%`);
        if (data.valid_to) badgeBits.push(`≤ ${data.valid_to}`);
        updLine(idx, {
          raw_material_id: rmId,
          unit_cost: String(Number(finalRate).toFixed(2)),
          price_source: "price_list",
          price_valid_to: data.valid_to || "",
          price_badge: badgeBits.join(" · "),
          ...gstAutoFill,
        });
      } else {
        updLine(idx, { raw_material_id: rmId, price_source: "manual", price_valid_to: "", price_badge: "", ...gstAutoFill });
      }
    } catch {
      updLine(idx, { raw_material_id: rmId, price_source: "manual", price_valid_to: "", price_badge: "", ...gstAutoFill });
    }
  };
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const rmLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const totalValue = useMemo(
    () => (form.lines || []).reduce((s, l) => {
      const total = (Number(l.qty_required) || 0) + (Number(l.qty_adjustment) || 0);
      return s + total * (Number(l.unit_cost) || 0);
    }, 0),
    [form.lines],
  );

  const canSave = form.supplier_id && form.po_date &&
    form.lines.length > 0 &&
    // Feb 2026 · Every PO line must carry a positive Rate. Blank / zero
    // rate slipped through in earlier releases, producing PDFs with an
    // empty Rate column and downstream valuation issues.
    form.lines.every(l => l.raw_material_id && Number(l.qty_required) > 0 && Number(l.unit_cost) > 0);

  const buildCreatePayload = () => ({
        supplier_id: form.supplier_id,
        bill_from_address_id: form.bill_from_address_id || "",
        ship_from_address_id: form.ship_from_address_id || "",
        po_date: form.po_date,
        expected_date: form.expected_date || "",
        remarks: form.remarks || "",
        terms_and_conditions: form.terms_and_conditions || "",
        tnc_template_id: form.tnc_template_id || "",
        // Phase H1-η · D-η4 · explicit procurement_source
        procurement_source: form.procurement_source || "DIRECT",
        source_pr_id: form.source_pr_id || "",
        source_pr_no: form.source_pr_no || "",
        source_mrp_no: form.source_mrp_no || "",
        source_work_order_id: form.source_work_order_id || "",
        source_work_order_no: form.source_work_order_no || "",
        source_sales_order_id: form.source_sales_order_id || "",
        source_sales_order_no: form.source_sales_order_no || "",
        lines: form.lines.map(l => {
          const req = Number(l.qty_required) || 0;
          const adj = Number(l.qty_adjustment) || 0;
          return {
            raw_material_id: l.raw_material_id,
            qty_ordered: req + adj,            // canonical
            qty_required: req,
            qty_adjustment: adj,
            unit_cost: Number(l.unit_cost || 0),
            hsn_code: (l.hsn_code || "").trim(),
            // PO-GST-01 · P2-C1-FIX / P2-D1-C · blank GST stays missing (null), never a silent 0%;
            // a 0% is confirmed only via the explicit checkbox (tax_pct 0 + gst_rate_source "edited").
            ...createLineGst(l),
            discount_pct: numOrNull(l.discount_pct),
            discount_amt: numOrNull(l.discount_amt),
            adjustment_notes: l.adjustment_notes || "",
            remarks: l.remarks || "",
            expected_date: l.expected_date || "",
            // Phase H1-η · Required-vs-Actual duality on the PO line.
            required_rm_id: l.required_rm_id || "",
            required_rm_code: l.required_rm_code || "",
            source_pr_line_no: Number(l.source_pr_line_no || 0),
            allocation_type: l.allocation_type ||
              (form.procurement_source === "DIRECT" ? "DIRECT_PURCHASE" : ""),
            alternate_approval_id: l.alternate_approval_id || "",
            alternate_approval_no: l.alternate_approval_no || "",
            alternate_priority: Number(l.alternate_priority || 0),
          };
        }),
        ...gstHeaderPayload(form),
  });
  // PO-GST-01 · P2-D1-C · authoritative read-only GST preview (POST /purchase-orders/gst-preview).
  const createPreview = useGstPreview({ create: buildCreatePayload() }, dialogOpen && !!canSave);
  const createPv = previewResult(createPreview.data);
  const pvLine = (i) => (createPreview.data?.lines || []).find(x => x.line_no === i + 1);
  const pvSource = (i) => {
    const li = createPreview.data?.line_inputs?.[i];
    if (!li) return "";
    return li.tax_pct == null ? "GST unresolved" : `${li.tax_pct}% · ${li.gst_rate_source || "—"}`;
  };

  const submit = async (e, submitAfter = false) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canSave) { toast.error("Supplier, date, RM, qty and Rate (> 0) are required on every line"); return; }
    // Feb 2026 · Extra guard for blank Rate — surface the exact
    // line number so operators can fix it in one click.
    const blankRateLine = form.lines.findIndex(l => !(Number(l.unit_cost) > 0));
    if (blankRateLine >= 0) {
      toast.error(`Line ${blankRateLine + 1}: Rate must be greater than zero`);
      return;
    }
    setSaving(true);
    try {
      const payload = buildCreatePayload();
      const { data } = await api.post("/purchase-orders/", payload);
      if (submitAfter) {
        try {
          await api.post(`/purchase-orders/${data.id}/submit-for-approval`);
          toast.success(`PO ${data.po_no} · Submitted for Authorisation`);
        } catch (se) {
          toast.error("PO was saved as Draft, but Submit for Authorisation failed. You can submit it from the list.");
        }
      } else {
        toast.success(`PO ${data.po_no} created · ${data.lines?.length || 0} line(s)`);
      }
      // Phase H1-η · D-η9 · Surface commercial warnings inline on success.
      if (Array.isArray(data.line_warnings) && data.line_warnings.length > 0) {
        data.line_warnings.forEach(w => {
          const label = w.code === "COMMERCIAL_RATE_VARIANCE"
            ? `Line ${w.line_no} · Rate variance ${w.variance_pct}% ≥ ${w.variance_threshold_pct}%`
            : w.code === "NO_ACTIVE_FROZEN_RATE"
              ? `Line ${w.line_no} · No frozen rate on record`
              : w.code === "COMMERCIAL_RATE_EXPIRED"
                ? `Line ${w.line_no} · Frozen rate expired`
                : `Line ${w.line_no} · ${w.code}`;
          toast.warning(label);
        });
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      // Phase H1-η · D-η9 · If the backend returns a
      // `PO_VALIDATION_FAILED` envelope, render each line failure
      // individually. Do NOT collapse to a generic toast.
      const detail = err?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "PO_VALIDATION_FAILED") {
        (detail.line_failures || []).forEach(f => {
          const msg = f.code === "PO_TECHNICAL_APPROVAL_MISSING"
            ? `Line ${f.line_no} · Alternate RM not approved for this Product`
            : f.code === "PO_SUPPLIER_MISMATCH"
              ? `Line ${f.line_no} · Supplier does not match Actual RM source`
              : f.code === "PROCUREMENT_SOURCE_AMBIGUOUS"
                ? `Line ${f.line_no} · Procurement source ambiguous — attach a PR or set procurement to DIRECT`
                : `Line ${f.line_no} · ${f.code} · ${f.reason || ""}`;
          toast.error(msg);
        });
      } else {
        toast.error(apiErrorMessage(err, "Save failed"));
      }
    } finally { setSaving(false); }
  };

  const openPdf = (po) => {
    // Feb 2026 · Route PO print via the shared DocumentPrintTemplate so
    // the browser sees the same A4 HTML layout used for PI + SO. Legacy
    // reportlab PDF endpoint is still available for archival downloads.
    printDocumentHtml({ kind: "po", id: po.id });
  };
  const openLegacyPdf = (po) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const tok = getToken();
    fetch(`${backend}/api/purchase-orders/${po.id}/pdf`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.blob()).then(b => window.open(URL.createObjectURL(b), "_blank"))
      .catch(() => toast.error("Failed to open PDF"));
  };
  // Edit mode inside detail dialog (Adjustment Qty + Notes only)
  const [editMode, setEditMode] = useState(false);
  const [editLines, setEditLines] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editHdr, setEditHdr] = useState(null);
  const gstPo = isGstControlled(detail);
  // PO-GST-01 · P2-D1-B-FIX01 · row-menu "Edit adjustments" waits for authoritative PO Detail eligibility.
  const pendingEditRef = useRef(false);
  const eligibilityLoading = !!detail && detail.has_posted_grn_history === undefined;
  // PO-GST-01 · P2-D1-B · J2 · read-only historical posted-GRN flag, fetched from the PO Detail
  // response (list rows / mutation responses do not carry it). Fail-closed until known.
  const grnLocked = !!detail && detail.has_posted_grn_history !== false;
  useEffect(() => {
    if (!detail?.id || detail.has_posted_grn_history !== undefined) return;
    let live = true;
    api.get(`/purchase-orders/${detail.id}`).then(({ data }) => {
      if (!live) return;
      setDetail(d => (d && d.id === data.id ? data : d));
      if (data.has_posted_grn_history) setEditMode(false);
    }).catch((e) => {
      if (!live || !pendingEditRef.current) return;
      pendingEditRef.current = false;
      toast.error(apiErrorMessage(e, "Could not verify edit eligibility"));
    });
    return () => { live = false; };
  }, [detail?.id, detail?.has_posted_grn_history]); // eslint-disable-line react-hooks/exhaustive-deps

  // PO-GST-01 · P2-D1-D · controlled in-app Cancel Draft dialog (replaces window.prompt).
  const [cancelAmdOpen, setCancelAmdOpen] = useState(false);
  const cancelDraftAmendment = () => {
    const amd = activeAmendment(detail);
    if (!amd || amd.state !== "Draft") return;
    setCancelAmdOpen(true);
  };
  const afterAmendCancel = async () => {
    setAmendOpen(false);
    const { data } = await api.get(`/purchase-orders/${detail.id}`);
    setDetail(data); load();
  };

  const canDirectEdit = (d) => !!d && !["Received", "Cancelled", "Pending Admin Approval"].includes(d.status) && !isAuthorisedPo(d);
  const startEdit = () => {
    if (!detail || detail.has_posted_grn_history !== false || !canDirectEdit(detail)) return;
    setEditLines((detail.lines || []).map(l => ({
      line_no: l.line_no,
      qty_adjustment: l.qty_adjustment ?? 0,
      adjustment_notes: l.adjustment_notes ?? "",
      unit_cost: l.unit_cost ?? 0,
      expected_date: (l.expected_date || "").slice(0, 10),
      // PO-GST-01 · P2-D1-C · GST inputs (sent only for GST-controlled POs, only when changed).
      hsn_code: l.hsn_code || "",
      tax_pct: l.tax_pct ?? "",
      zero_confirmed: l.tax_pct === 0 && !!l.gst_rate_source,
      discount_pct: l.discount_pct ?? "",
      discount_amt: l.discount_amt ?? "",
      gst_dirty: false,
    })));
    setEditHdr(gstHeaderFromPo(detail));
    setEditMode(true);
  };
  const openEditFromRow = (p) => {
    setEditMode(false);
    pendingEditRef.current = true;
    setDetail({ ...p, has_posted_grn_history: undefined });
  };
  useEffect(() => {
    if (!pendingEditRef.current || !detail || detail.has_posted_grn_history === undefined) return;
    pendingEditRef.current = false;
    if (detail.has_posted_grn_history) { toast.error(GRN_LOCK_TEXT); return; }
    if (!canDirectEdit(detail)) { toast.info("Direct edit is not available for this PO — see PO Detail actions."); return; }
    startEdit();
  }, [detail]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancelEdit = () => { setEditMode(false); setEditLines([]); setEditHdr(null); };
  const updEditLine = (i, patch) => setEditLines(prev => prev.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const buildEditBody = () => {
    const rateEditable = !(detail.lines || []).some(l => Number(l.qty_received || 0) > 0);
    return {
      ...(gstPo ? { expected_revision: Number(detail.revision || 0), ...gstHeaderDiff(editHdr || {}, detail) } : {}),
      lines: editLines.map(l => {
        const orig = (detail.lines || []).find(x => x.line_no === l.line_no) || {};
        return {
          line_no: l.line_no,
          qty_adjustment: Number(l.qty_adjustment) || 0,
          adjustment_notes: l.adjustment_notes || "",
          expected_date: l.expected_date || "",
          ...(rateEditable ? { unit_cost: Number(l.unit_cost) || 0 } : {}),
          ...(gstPo ? editLineGst(l, orig) : {}),
        };
      }),
    };
  };
  const saveEdit = async () => {
    if (!detail) return;
    // Feb 2026 · Rate edit is allowed until ANY receipt is posted (mirror
    // backend gate in purchase_orders.py). Was previously Draft-only.
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/purchase-orders/${detail.id}`, buildEditBody());
      toast.success(`${data.po_no} updated`);
      setDetail(data); setEditMode(false); setEditLines([]); setEditHdr(null); load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSavingEdit(false); }
  };
  const editPreview = useGstPreview(editMode && gstPo && detail ? { po_id: detail.id, edit: buildEditBody() } : null,
                                    editMode && gstPo && !!detail);
  const editSource = (lineNo) => {
    const li = (editPreview.data?.line_inputs || []).find(x => x.line_no === lineNo);
    if (!li) return "";
    return li.tax_pct == null ? "GST unresolved" : `${li.tax_pct}% · ${li.gst_rate_source || "—"}`;
  };

  // PO-AUTH-EDIT-AMEND-H01 · Phase 2B.2 · Feb 2026 ─────────────────
  // Amendment lifecycle wiring. All mutations route through the
  // canonical Phase 2B.1 backend endpoints — the frontend NEVER edits
  // live PO fields; only `pending_changes` are pushed via
  //   POST /purchase-orders/{id}/amend
  //   PUT  /purchase-orders/{id}/amend/pending
  //   POST /purchase-orders/{id}/amend/submit
  // Approve/Reject route through /approvals/PO/{id}/{approve|reject}
  // and are amendment-aware server-side.
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendHistoryOpen, setAmendHistoryOpen] = useState(false);
  const refreshDetail = async () => {
    if (!detail?.id) return;
    try {
      const { data } = await api.get(`/purchase-orders/${detail.id}`);
      setDetail(data);
    } catch { /* noop */ }
  };
  const openAmendment = async () => {
    if (!detail) return;
    const amd = activeAmendment(detail);
    if (amd) {
      // Existing Draft → open the editor directly (no server call)
      if (amd.state === "Draft") { setAmendOpen(true); return; }
      // Pending Re-authorisation → read-only info until Management acts
      toast.info(`${amd.amendment_no} is pending Management Re-authorisation`);
      return;
    }
    try {
      await api.post(`/purchase-orders/${detail.id}/amend`);
      await refreshDetail();
      setAmendOpen(true);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to create amendment"));
    }
  };

  // Filter list by search — matches PO no, supplier name, status, or any line's RM code / name.
  const filtered = useMemo(() => {
    const needle = poSearch.trim().toLowerCase();
    if (!needle) return pos;
    return pos.filter(p => {
      if ([p.po_no, p.supplier_name, p.status].filter(Boolean).join(" ").toLowerCase().includes(needle)) return true;
      return (p.lines || []).some(l => [l.rm_code, l.rm_name].filter(Boolean).join(" ").toLowerCase().includes(needle));
    });
  }, [pos, poSearch]);

  const exportCsv = () => {
    const rows = [];
    filtered.forEach(p => {
      (p.lines || []).forEach(l => {
        rows.push({
          po_no: p.po_no, po_date: (p.po_date || "").slice(0, 10),
          supplier: p.supplier_name, expected: fmtDateIST(p.expected_date),
          so_type: (p.source_sales_order_no || "").startsWith("ESO") ? "ESO" : (p.source_sales_order_no ? "SO" : ""),
          so_no: p.source_sales_order_no || "",
          wo_no: p.source_work_order_no || "",
          pr_no: p.source_pr_no || "", mrp_no: p.source_mrp_no || "",
          line_no: l.line_no, rm_code: l.rm_code, rm_name: l.rm_name,
          required: l.qty_required ?? l.qty_ordered ?? 0,
          adjustment: l.qty_adjustment || 0,
          total: l.qty_total ?? l.qty_ordered ?? 0,
          ordered: l.qty_ordered, received: l.qty_received || 0,
          pending: l.qty_pending || 0, unit_cost: l.unit_cost || 0,
          value: (l.qty_ordered || 0) * (l.unit_cost || 0),
          adj_notes: l.adjustment_notes || "",
          status: l.status, header_status: p.status,
        });
      });
    });
    exportToCSV("purchase-orders.csv", rows, [
      { key: "po_no", label: "PO No" }, { key: "po_date", label: "PO Date" },
      { key: "supplier", label: "Supplier" }, { key: "expected", label: "Expected" },
      { key: "so_type", label: "SO Type" }, { key: "so_no", label: "SO / ESO No" },
      { key: "wo_no", label: "Work Order" },
      { key: "pr_no", label: "Source PR" }, { key: "mrp_no", label: "MRP Run" },
      { key: "line_no", label: "#" }, { key: "rm_code", label: "RM Code" },
      { key: "rm_name", label: "Name" },
      { key: "required", label: "Required" }, { key: "adjustment", label: "Adjustment" },
      { key: "total", label: "Total" },
      { key: "ordered", label: "Ordered" },
      { key: "received", label: "Received" }, { key: "pending", label: "Pending" },
      { key: "unit_cost", label: "Unit Cost" }, { key: "value", label: "Line Value" },
      { key: "adj_notes", label: "Adjustment Notes" },
      { key: "status", label: "Line Status" }, { key: "header_status", label: "PO Status" },
    ]);
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="p-6 space-y-5" data-testid="purchase-orders-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Inventory · Procurement</div>
          <h1 className="font-display text-3xl font-black tracking-tight">Purchase Orders</h1>
          <p className="text-xs text-zinc-500 mt-1">Supplier commitments · GRN entries auto-close matched lines here · overdue rows flagged in amber.</p>
        </div>
        <div className="flex items-center gap-2">
          {subTab === "orders" && <>
          <button data-testid="new-po-btn" onClick={openNew}
            className="h-10 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" />New Purchase Order
          </button>
          <button data-testid="po-export" onClick={exportCsv} disabled={filtered.length === 0}
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40">
            <DownloadSimpleIcon size={14} weight="bold" />Export CSV
          </button>
          </>}
        </div>
      </div>

      {/* Mar 3, 2026 · Sub-tabs — Orders / Status Report */}
      <div className="flex gap-2 border-b border-[var(--border)]">
        {[
          { key: "orders", label: "Orders" },
          { key: "report", label: "Status Report" },
        ].map(t => (
          <button
            key={t.key}
            data-testid={`po-subtab-${t.key}`}
            onClick={() => setSubTabPersist(t.key)}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${subTab === t.key ? "border-[var(--accent)] text-white" : "border-transparent text-zinc-400 hover:text-white"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "report" && <PoStatusReport />}

      {subTab === "orders" && (<>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="tactical-card p-4"><div className="overline mb-1">Total POs</div><div className="font-mono text-3xl font-semibold text-white">{pos.length}</div></div>
        <div className="tactical-card p-4 border-l-2 border-l-sky-500"><div className="overline text-sky-300 mb-1">Open</div><div className="font-mono text-3xl font-semibold text-sky-400">{pos.filter(p => p.status === "Open").length}</div></div>
        <div className="tactical-card p-4 border-l-2 border-l-amber-500"><div className="overline text-amber-300 mb-1">Partial</div><div className="font-mono text-3xl font-semibold text-amber-400">{pos.filter(p => p.status === "Partially Received").length}</div></div>
        <div className="tactical-card p-4 border-l-2 border-l-emerald-500"><div className="overline text-emerald-300 mb-1">Received</div><div className="font-mono text-3xl font-semibold text-emerald-400">{pos.filter(p => p.status === "Received").length}</div></div>
      </div>

      {/* Search — mirrors Sales Orders */}
      <div className="flex items-center gap-2">
        <div className="relative w-full max-w-md">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="po-search"
            value={poSearch}
            onChange={e => setPoSearch(e.target.value)}
            placeholder="Search PO no, supplier, RM code, status…"
            className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm placeholder:text-zinc-500 focus:outline-none focus:border-[var(--accent)]"
          />
        </div>
        {poSearch && (
          <button data-testid="po-search-clear" onClick={() => setPoSearch("")}
            className="h-9 px-3 text-xs text-zinc-400 hover:text-white border border-[var(--border)] rounded-sm">Clear</button>
        )}
        <span className="ml-auto text-xs text-zinc-500">{filtered.length} PO(s) · {filtered.reduce((s, p) => s + (p.lines || []).length, 0)} line(s)</span>
      </div>

      {/* SO-style line-per-row grid */}
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="px-3 py-3 text-left font-medium">PO No</th>
              <th className="px-3 py-3 text-left font-medium">PO Date</th>
              <th className="px-3 py-3 text-left font-medium">Supplier</th>
              <th className="px-3 py-3 text-left font-medium">SO / ESO</th>
              <th className="px-3 py-3 text-left font-medium">Work Order</th>
              <th className="px-3 py-3 text-left font-medium">Item No</th>
              <th className="px-3 py-3 text-left font-medium">RM Code</th>
              <th className="px-3 py-3 text-left font-medium">Name</th>
              <th className="px-3 py-3 text-left font-medium">UOM</th>
              <th className="px-3 py-3 text-right font-medium">Order Qty</th>
              <th className="px-3 py-3 text-right font-medium">Received</th>
              <th className="px-3 py-3 text-right font-medium">Pending</th>
              <th className="px-3 py-3 text-right font-medium">Unit Cost</th>
              <th className="px-3 py-3 text-right font-medium">Line Value</th>
              <th className="px-3 py-3 text-left font-medium">Expected</th>
              <th className="px-3 py-3 text-left font-medium">Status</th>
              <th className="px-3 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pos.length === 0 && (
              <tr><td colSpan={15} className="text-center py-16 text-zinc-500">
                No purchase orders yet — click <b className="text-white">New Purchase Order</b> to add one.
              </td></tr>
            )}
            {pos.length > 0 && filtered.length === 0 && (
              <tr><td colSpan={17} className="text-center py-10 text-zinc-500">No POs match "{poSearch}"</td></tr>
            )}
            {filtered.flatMap(p => {
              const lines = (p.lines || []);
              const linesToRender = lines.length ? lines : [null];
              const isOverdue = p.expected_date && p.expected_date < today && p.status !== "Received" && p.status !== "Cancelled";
              // Feb 2026 · Cancelled POs stay in the grid but get a subtle
              // red strikethrough so dead documents are instantly spotted
              // without hunting through the Status column. Matches SO/ESO.
              const isPoCancelled = p.status === "Cancelled";
              return linesToRender.map((l, lineIdx) => {
                const isFirst = lineIdx === 0;
                const isLast = lineIdx === linesToRender.length - 1;
                const lineValue = (Number(l?.qty_ordered) || 0) * (Number(l?.unit_cost) || 0);
                return (
                  <tr
                    key={`${p.id}-${lineIdx}`}
                    data-testid={`po-line-${p.po_no}-${lineIdx}`}
                    className={`${!isLast ? "border-b border-[var(--border)]/40" : "border-b border-[var(--border)]"} align-top ${
                      isOverdue ? "bg-amber-500/[0.06] hover:bg-amber-500/[0.09]" : "hover:bg-[var(--bg-2)]/40"
                    } ${isPoCancelled ? "[&>td]:line-through [&>td]:text-red-400/70 opacity-80" : ""} ${isFirst && flashingPoIds.has(p.id) ? "row-flash-emerald" : ""}`}
                  >
                    <td className="px-3 py-3 font-mono text-[var(--accent)] text-xs whitespace-nowrap">
                      {isFirst ? (
                        <div className="flex flex-col gap-1">
                          <span>{p.po_no}</span>
                          {revisionLabel(p) && (<span data-testid={`po-row-revision-${p.po_no}`} className="inline-flex items-center px-1.5 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[9px] font-mono uppercase tracking-widest w-fit">{revisionLabel(p)}</span>)}
                          {amendState(p) && (<span data-testid={`po-row-amend-${p.po_no}`} className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-widest w-fit ${amendState(p).cls}`}>{amendState(p).label}</span>)}
                        </div>
                      ) : <span className="text-zinc-700">↳</span>}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-zinc-400">
                      {isFirst ? fmtDateIST(p.po_date) : ""}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      {isFirst ? <span className="text-white">{p.supplier_name || "—"}</span> : ""}
                    </td>
                    <td className="px-3 py-3 text-xs" data-testid={isFirst ? `po-so-${p.po_no}` : undefined}>
                      {isFirst && p.source_sales_order_no ? (
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full border uppercase tracking-widest ${(p.source_sales_order_no || "").startsWith("ESO") ? "bg-blue-500/10 text-blue-300 border-blue-500/40" : "bg-purple-500/10 text-purple-300 border-purple-500/40"}`}>
                          {(p.source_sales_order_no || "").startsWith("ESO") ? "ESO" : "SO"} · {p.source_sales_order_no}
                        </span>
                      ) : isFirst ? <span className="text-zinc-600">—</span> : ""}
                    </td>
                    <td className="px-3 py-3 text-xs" data-testid={isFirst ? `po-wo-${p.po_no}` : undefined}>
                      {isFirst && p.source_work_order_no ? (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full border bg-emerald-500/10 text-emerald-300 border-emerald-500/40 uppercase tracking-widest">
                          WO · {p.source_work_order_no}
                        </span>
                      ) : isFirst ? <span className="text-zinc-600">—</span> : ""}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-zinc-500">{l ? String(l.line_no).padStart(3, "0") : "—"}</td>
                    <td className="px-3 py-3 font-mono text-[var(--accent)] text-xs whitespace-nowrap">{l?.rm_code || "—"}</td>
                    <td className="px-3 py-3 text-xs">{l?.rm_name || "—"}</td>
                    <td className="px-3 py-3 font-mono text-xs text-zinc-400">{l?.uom || "—"}</td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums text-sm">{l ? fmtQty(l.qty_ordered) : "—"}</td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums text-sm text-emerald-300">{l ? fmtQty(l.qty_received || 0) : "—"}</td>
                    <td className={`px-3 py-3 text-right font-mono tabular-nums text-sm ${Number(l?.qty_pending) > 0 ? "text-amber-300" : "text-zinc-500"}`}>{l ? fmtQty(l.qty_pending || 0) : "—"}</td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums text-xs text-zinc-400">{l?.unit_cost ? `₹${Number(l.unit_cost).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums text-xs text-zinc-300">{l ? `₹${Number(lineValue).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>
                    <td className="px-3 py-3 font-mono text-xs text-zinc-400 whitespace-nowrap">
                      {isFirst ? (
                        <span className={isOverdue ? "text-amber-300 font-semibold" : ""}>
                          {fmtDateIST(p.expected_date)}
                          {isOverdue && <span className="ml-1.5 text-[9px] uppercase tracking-widest">Overdue</span>}
                        </span>
                      ) : ""}
                    </td>
                    <td className="px-3 py-3">
                      {/* Feb 2026 · Show header PO status on every row so
                          Draft (after Unapprove) / Short Closed / Cancelled
                          / Received flow up to the grid. Line-level status
                          (`l.status`) is preserved in the CSV export. */}
                      {isFirst ? (
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${badgeCls(p.status)}`}>
                            {p.status}
                          </span>
                          <span
                            data-testid={`po-auth-badge-${p.po_no}`}
                            className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${authBadgeCls(authState(p))}`}
                          >
                            {authState(p)}
                          </span>
                          {amendPendingLabel(p) && (
                            <span
                              data-testid={`po-row-status-amend-pending-${p.po_no}`}
                              className="inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest border-amber-500/50 bg-amber-500/10 text-amber-300"
                              title="An Amendment against this PO is awaiting Management Authorisation. The effective PO remains Authorised at the current revision until the Amendment is Authorised."
                            >
                              {amendPendingLabel(p)}
                            </span>
                          )}
                        </div>
                      ) : (
                        l && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${badgeCls(l.status)}`}>
                            {l.status}
                          </span>
                        )
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {isFirst && (
                        <RowActions
                          testid={`po-actions-${p.po_no}`}
                          items={[
                            { label: "View PO", testid: `po-view-${p.po_no}`, onClick: () => setDetail(p) },
                            { label: "Edit adjustments", testid: `po-edit-${p.po_no}`, show: p.status !== "Received" && p.status !== "Cancelled", onClick: () => openEditFromRow(p) },
                            { label: "Print to PDF", testid: `po-pdf-${p.po_no}`, onClick: () => openPdf(p) },
                            {
                              label: "Reverse Cancel", tone: "approve", testid: `po-reverse-${p.po_no}`,
                              show: p.status === "Cancelled",
                              onClick: async () => {
                                const reason = window.prompt(`Reactivate ${p.po_no}? Enter reason for the audit log:`, "");
                                if (!reason || !reason.trim()) return;
                                try {
                                  const { data } = await api.post(`/purchase-orders/${p.id}/reverse-cancel`, { reason: reason.trim() });
                                  toast.success(`${p.po_no} reactivated → ${data.status}`);
                                  load();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Reverse failed");
                                }
                              },
                            },
                            { label: "Cancel PO", tone: "warn", testid: `po-cancel-${p.po_no}`, show: p.status !== "Cancelled", onClick: () => setCancelTarget({ id: p.id, po_no: p.po_no }) },
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>

      </>)}

      {/* New PO Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-[95vw] max-h-[92vh] overflow-y-auto"
          data-testid="po-dialog"
          onPointerDownOutside={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
          onEscapeKeyDown={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <ClipboardTextIcon size={20} weight="bold" className="text-[var(--accent)]" />
              New Purchase Order — Manual Entry
            </DialogTitle>
          </DialogHeader>
          <form id="po-form" onSubmit={submit} className="space-y-4 mt-2">
            {/* Phase H1-η · D-η4 · procurement_source explicit toggle.
                PR-backed conversions use /prs/{pr_id}/convert-to-po and
                aren't reachable from this dialog. */}
            <div className="flex items-center gap-2 border border-[var(--border)] rounded-sm px-3 py-2 bg-[var(--bg-0)]/40">
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Procurement Source</span>
              <div className="flex gap-1" role="radiogroup" aria-label="Procurement Source">
                <button type="button"
                        data-testid="po-procurement-pr"
                        onClick={() => setForm(f => ({ ...f, procurement_source: "PR" }))}
                        aria-checked={form.procurement_source === "PR"}
                        role="radio"
                        className={`px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${form.procurement_source === "PR" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40" : "border-[var(--border)] text-zinc-400 hover:bg-[var(--bg-2)]"}`}>
                  PR-BACKED
                </button>
                <button type="button"
                        data-testid="po-procurement-direct"
                        onClick={() => { setForm(f => ({ ...f, procurement_source: "DIRECT", source_pr_id: "", source_pr_no: "", source_mrp_no: "", source_work_order_id: "", source_work_order_no: "", source_sales_order_id: "", source_sales_order_no: "", lines: [emptyLine()] })); setSrcPr(null); }}
                        aria-checked={form.procurement_source === "DIRECT"}
                        role="radio"
                        className={`px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${form.procurement_source === "DIRECT" ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/40" : "border-[var(--border)] text-zinc-400 hover:bg-[var(--bg-2)]"}`}>
                  DIRECT PURCHASE
                </button>
              </div>
              <span className="ml-2 text-[10px] font-mono text-zinc-500 leading-tight">
                {form.procurement_source === "PR"
                  ? "PR-backed · pick a source PR below · line RM picker restricted to Required ∪ valid alternates."
                  : "Direct purchases carry no Required RM / RMA / EXACT / ALT genealogy · backend stamps DIRECT_PURCHASE."}
              </span>
            </div>

            {form.procurement_source === "PR" && (
              <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm px-3 py-2 flex items-center gap-3 flex-wrap"
                    data-testid="po-source-pr-block">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Source PR</span>
                <select
                  data-testid="po-source-pr-picker"
                  value={form.source_pr_id || ""}
                  onChange={e => pickSourcePr(e.target.value)}
                  className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono min-w-[240px]">
                  <option value="">— select PR —</option>
                  {openPrs.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.pr_no} · {p.product_code || "—"} · {(p.lines || []).length} line(s)
                    </option>
                  ))}
                </select>
                {srcPr && (
                  <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-400 flex-wrap">
                    <span>MRP <span className="text-white">{srcPr.mrp_no || "—"}</span></span>
                    <span>·</span>
                    <span>WO <span className="text-white">{srcPr.work_order_no || "—"}</span></span>
                    <span>·</span>
                    <span>Customer <span className="text-white truncate max-w-[180px]">{srcPr.customer || "—"}</span></span>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className={lblCls}>Supplier *</label>
                <SearchablePicker
                  testid="po-supplier"
                  value={form.supplier_id}
                  onChange={(v) => {
                    // Feb 2026 · On supplier change, auto-preselect the
                    // supplier's primary Bill-From / Ship-From so the PDF
                    // has a full address block even without picker clicks.
                    const s = suppliers.find(x => x.id === v);
                    const addrs = (s?.addresses) || [];
                    const pb = addrs.find(a => a.is_primary_bill) || addrs[0];
                    const ps = addrs.find(a => a.is_primary_ship) || pb;
                    setForm(f => ({ ...f, supplier_id: v,
                      bill_from_address_id: pb?.id || "",
                      ship_from_address_id: ps?.id || "" }));
                  }}
                  placeholder="Select supplier…"
                  headerHint="Search by supplier code or name"
                  options={suppliers.map(s => ({ value: s.id, code: s.code, name: s.name }))}
                />
              </div>
              {(() => {
                const sup = suppliers.find(x => x.id === form.supplier_id);
                const addrs = (sup?.addresses) || [];
                if (!sup || !addrs.length) return null;
                const label = (a) => `${a.custom_label || a.label} · ${(a.city || a.state || "").slice(0, 24)}${a.gstin ? " · " + a.gstin : ""}`;
                const bill = addrs.find(a => a.id === form.bill_from_address_id) || addrs[0];
                const gstin = bill?.gstin || sup.gstin || "";
                return (<>
                  <div>
                    <label className={lblCls}>Bill From Address</label>
                    <select data-testid="po-bill-from" value={form.bill_from_address_id}
                      onChange={e => setForm(f => ({ ...f, bill_from_address_id: e.target.value }))}
                      className={inpCls}>
                      {addrs.map(a => <option key={a.id} value={a.id}>{label(a)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lblCls}>Ship From Address</label>
                    <select data-testid="po-ship-from" value={form.ship_from_address_id}
                      onChange={e => setForm(f => ({ ...f, ship_from_address_id: e.target.value }))}
                      className={inpCls}>
                      {addrs.map(a => <option key={a.id} value={a.id}>{label(a)}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-4 text-[10px] font-mono text-zinc-400 -mt-1" data-testid="po-supplier-gstin">
                    {gstin ? <>GSTIN: <span className="text-emerald-300">{gstin}</span></> : "GSTIN: —"}
                    {bill?.address && <> · {bill.address.replace(/\n/g, ", ")}</>}
                  </div>
                </>);
              })()}
              <div>
                <label className={lblCls}>PO Date *</label>
                <input data-testid="po-date" type="date" value={form.po_date}
                  onChange={e => setForm(f => ({ ...f, po_date: e.target.value }))}
                  className={inpCls} required />
              </div>
              <div>
                <label className={lblCls}>Expected Delivery</label>
                <div className="flex items-center gap-1">
                  <input data-testid="po-expected" type="date" value={form.expected_date}
                    onChange={e => setForm(f => ({ ...f, expected_date: e.target.value }))}
                    className={inpCls + " flex-1"} />
                  <button
                    type="button"
                    data-testid="po-bulk-line-date-btn"
                    disabled={!form.expected_date || !(form.items || []).length}
                    onClick={() => {
                      // Feb 2026 · Bulk Line Date — force every RM line's
                      // `expected_delivery` (per-line override) to the
                      // header Expected Delivery. Overwrites even lines
                      // that already carry a different date; handy when a
                      // single vendor commit covers every line.
                      const target = form.expected_date;
                      const items = form.items || [];
                      if (!target) { toast.error("Set an Expected Delivery date first"); return; }
                      if (!items.length) { toast.error("Add at least one line item"); return; }
                      setForm(f => ({ ...f, items: (f.items || []).map(it => ({ ...it, expected_delivery: target })) }));
                      toast.success(`Applied ${target} to ${items.length} line${items.length === 1 ? "" : "s"}`);
                    }}
                    title="Force every line's Expected Delivery to the header value"
                    className="h-9 px-2 text-[10px] font-semibold uppercase tracking-widest border border-[var(--border)] bg-[var(--bg-2)]/60 text-zinc-300 hover:text-white hover:border-[var(--accent)]/60 hover:bg-[var(--accent)]/10 rounded-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Apply&nbsp;All
                  </button>
                </div>
              </div>
              <div>
                <label className={lblCls}>PO No</label>
                <input value="Auto-assigned on Save" disabled
                  className={`${inpCls} text-zinc-500 italic`} />
              </div>
              <div className="md:col-span-4">
                <label className={lblCls}>Remarks</label>
                <input data-testid="po-remarks" type="text" value={form.remarks}
                  onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                  className={inpCls} placeholder="Terms, payment mode, notes…" />
              </div>
              {/* Feb 2026 · Purchase-side Terms & Conditions block —
                  templates filtered server-side to template_type=purchase
                  so sales T&Cs never surface here. Default template is
                  pre-loaded when the dialog opens. */}
              <div className="md:col-span-4">
                <div className="flex items-center gap-2 mb-1">
                  <label className={`${lblCls} !mb-0 flex-1`}>Terms &amp; Conditions</label>
                  <select
                    data-testid="po-tnc-template"
                    className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs"
                    value={form.tnc_template_id || ""}
                    onChange={e => {
                      const id = e.target.value;
                      const tpl = tncTemplates.find(t => t.id === id);
                      setForm(f => ({
                        ...f,
                        tnc_template_id: id,
                        terms_and_conditions: tpl ? tpl.text : (f.terms_and_conditions || ""),
                      }));
                    }}
                  >
                    <option value="">— Pick a template —</option>
                    {tncTemplates.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name}{t.is_default ? " · default" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  data-testid="po-tnc-text"
                  rows={4}
                  value={form.terms_and_conditions || ""}
                  onChange={e => setForm(f => ({ ...f, terms_and_conditions: e.target.value }))}
                  className="w-full min-h-[90px] px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono resize-y focus:outline-none focus:border-[var(--accent)]"
                  placeholder="Pick a template or type your own T&C — printed above the signature line on the PO PDF"
                />
              </div>
            </div>

            <div className="border-t border-[var(--border)] pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="overline">Line Items · {form.lines.length}</div>
                <button type="button" data-testid="po-add-line" onClick={addLine}
                  className="h-8 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs rounded-sm flex items-center gap-1.5">
                  <PlusIcon size={11} weight="bold" />Add Line
                </button>
              </div>
              <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
                    <tr>
                      <th className="px-2 py-1.5 text-left w-8">#</th>
                      <th className="px-2 py-1.5 text-left min-w-[220px]">Raw Material *</th>
                      <th className="px-2 py-1.5 text-left w-24">HSN Code</th>
                      <th className="px-2 py-1.5 text-right w-24">Required *</th>
                      <th className="px-2 py-1.5 text-right w-24">Adjustment</th>
                      <th className="px-2 py-1.5 text-right w-24">Total</th>
                      <th className="px-2 py-1.5 text-right w-24">Unit Cost</th>
                      <th className="px-2 py-1.5 text-right w-16">Disc %</th>
                      <th className="px-2 py-1.5 text-right w-20">Disc ₹</th>
                      <th className="px-2 py-1.5 text-right w-28" title="Authoritative backend preview">Taxable Value</th>
                      <th className="px-2 py-1.5 text-right w-24">GST %</th>
                      <th className="px-2 py-1.5 text-right w-28" title="Authoritative backend preview">GST Amount</th>
                      <th className="px-2 py-1.5 text-right w-28" title="Authoritative backend preview">Line Total</th>
                      <th className="px-2 py-1.5 text-left w-36">Delivery Date</th>
                      <th className="px-2 py-1.5 text-left min-w-[180px]">Adj Notes</th>
                      <th className="px-2 py-1.5 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((l, i) => {
                      const rm = rms.find(r => r.id === l.raw_material_id);
                      const total = (Number(l.qty_required) || 0) + (Number(l.qty_adjustment) || 0);
                      const pl = pvLine(i);
                      const fmtPv = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
                      return (
                        <tr key={i} className="border-t border-[var(--border)]" data-testid={`po-form-line-${i}`}>
                          <td className="px-2 py-1.5 text-xs font-mono text-zinc-500">{String(i + 1).padStart(3, "0")}</td>
                          <td className="px-2 py-1.5">
                            {form.procurement_source === "PR" && l.required_rm_id ? (
                              <>
                                <SearchablePicker
                                  testid={`po-line-${i}-rm-restricted`}
                                  value={l.raw_material_id || ""}
                                  onChange={v => setPrBackedLineActualRm(i, v)}
                                  placeholder="Select Actual RM…"
                                  headerHint="Search by RM code, name, supplier or RMA"
                                  className="h-8 text-xs"
                                  popoverClassName="min-w-[640px]"
                                  options={[
                                    { value: l.required_rm_id, code: l.required_rm_code,
                                      name: rms.find(r => r.id === l.required_rm_id)?.name || "", meta: "EXACT" },
                                    ...(l._validAlts || []).map(a => ({
                                      value: a.alternate_rm_id, code: a.alternate_rm_code,
                                      name: `${a.alternate_supplier_name || a.alternate_supplier_id || "—"} · RMA ${a.rma_no}`,
                                      meta: `ALT P${a.priority}`,
                                    })),
                                  ]}
                                />
                                <div className="mt-0.5 flex items-center gap-1.5">
                                  <AllocationBadge type={l.allocation_type || "EXACT"} size="sm" priority={l.alternate_priority} />
                                  <span className="text-[9px] font-mono text-zinc-500">Required <span className="text-emerald-300">{l.required_rm_code}</span></span>
                                </div>
                              </>
                            ) : (
                              <>
                                <SearchablePicker
                                  testid={`po-line-${i}-rm`}
                                  value={l.raw_material_id || ""}
                                  onChange={v => priceLookup(i, v)}
                                  placeholder="Select RM…"
                                  headerHint="Search by RM code or name"
                                  className="h-8 text-xs"
                                  popoverClassName="min-w-[640px]"
                                  options={rms.map(r => ({ value: r.id, code: r.code, name: r.name, meta: r.uom || "" }))}
                                />
                                {form.procurement_source === "DIRECT" && l.raw_material_id && (
                                  <div className="mt-0.5 flex items-center gap-1.5">
                                    <AllocationBadge type="DIRECT_PURCHASE" size="sm" />
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            <input data-testid={`po-line-${i}-hsn`} type="text" maxLength={12}
                              value={l.hsn_code || ""} onChange={e => updLine(i, { hsn_code: e.target.value.replace(/\s+/g, "") })}
                              placeholder="58063200"
                              className={`${cellCls} font-mono text-xs`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <input data-testid={`po-line-${i}-req`} type="number" step="0.001" min="0"
                              value={l.qty_required} onChange={e => updLine(i, { qty_required: e.target.value })}
                              className={`${cellCls} text-right font-mono`} required />
                            {rm && <div className="text-[9px] text-zinc-500 text-right mt-0.5">{rm.uom}</div>}
                          </td>
                          <td className="px-2 py-1.5">
                            <input data-testid={`po-line-${i}-adj`} type="number" step="0.001"
                              value={l.qty_adjustment} onChange={e => updLine(i, { qty_adjustment: e.target.value })}
                              className={`${cellCls} text-right font-mono`} />
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-xs text-emerald-300 tabular-nums" data-testid={`po-line-${i}-total`}>{total.toFixed(3)}</td>
                          <td className="px-2 py-1.5">
                            <input data-testid={`po-line-${i}-cost`} type="number" step="0.01" min="0"
                              value={l.unit_cost} onChange={e => updLine(i, { unit_cost: e.target.value, price_source: "manual" })}
                              className={`${cellCls} text-right font-mono`} />
                            {l.price_source === "price_list" && (
                              <div className="text-[9px] text-emerald-300 text-right mt-0.5 font-mono tracking-widest" data-testid={`po-line-${i}-price-source`}>
                                PRICE LIST{l.price_badge ? ` · ${l.price_badge}` : (l.price_valid_to ? ` · ≤ ${l.price_valid_to}` : "")}
                              </div>
                            )}
                            {l.price_source === "manual" && l.raw_material_id && (
                              <div className="text-[9px] text-amber-300 text-right mt-0.5 font-mono tracking-widest" data-testid={`po-line-${i}-price-source`}>
                                MANUAL · NO AGREEMENT
                              </div>
                            )}
                          </td>
                          <DiscountCells line={l} onChange={patch => updLine(i, patch)} testid={`po-line-${i}`} />
                          <td className="px-2 py-1.5 text-right font-mono text-xs text-zinc-300 tabular-nums" data-testid={`po-line-${i}-taxable`}>{fmtPv(pl?.taxable_value)}</td>
                          <td className="px-2 py-1.5">
                            <GstRateCell line={l} onChange={patch => updLine(i, patch)} source={pvSource(i)} testid={`po-line-${i}`} />
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-xs text-amber-200 tabular-nums" data-testid={`po-line-${i}-gst-amt`}>{fmtPv(pl?.tax_amt)}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-xs text-white tabular-nums font-semibold" data-testid={`po-line-${i}-line-total`}>{fmtPv(pl?.line_total)}</td>
                          <td className="px-2 py-1.5">
                            {/* Mar 3, 2026 · Item-wise delivery date — mirrors SO/ESO.
                                Blank on a line falls back to header expected_date. */}
                            <input data-testid={`po-line-${i}-exp`} type="date"
                              value={l.expected_date || ""} onChange={e => updLine(i, { expected_date: e.target.value })}
                              min={form.po_date || undefined}
                              className={`${cellCls} text-xs`}
                              title="Optional · overrides the header Expected Date for this line" />
                          </td>
                          <td className="px-2 py-1.5">
                            <input data-testid={`po-line-${i}-notes`} type="text" maxLength={140}
                              value={l.adjustment_notes} onChange={e => updLine(i, { adjustment_notes: e.target.value })}
                              placeholder="e.g. MOQ 1 bag = 25 kg"
                              className={cellCls} />
                          </td>
                          <td className="px-2 py-1.5">
                            {form.lines.length > 1 && (
                              <button type="button" data-testid={`po-line-${i}-del`} onClick={() => rmLine(i)}
                                className="p-1 text-zinc-500 hover:text-red-400">
                                <TrashIcon size={12} weight="bold" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-[var(--border)] bg-[var(--bg-2)]/30">
                      <td colSpan={9} className="px-2 py-2 text-right text-xs uppercase tracking-widest text-zinc-500">Goods value (excl. GST) · Tax · Grand total (incl. GST)</td>
                      <td className="px-2 py-2 text-right font-mono text-xs text-zinc-300 tabular-nums" data-testid="po-total-taxable">{createPreview.data ? `₹${Number(createPreview.data.goods_total_value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>
                      <td></td>
                      <td className="px-2 py-2 text-right font-mono text-xs text-amber-200 tabular-nums" data-testid="po-total-gst">{createPv?.totals?.tax_total == null ? "—" : `₹${Number(createPv.totals.tax_total).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</td>
                      <td className="px-2 py-2 text-right font-mono text-sm text-white font-semibold" data-testid="po-total-value">{createPv?.totals?.grand_total == null ? "—" : `₹${Number(createPv.totals.grand_total).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</td>
                      <td colSpan={3}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <GstTermsSection testid="po-create-gst-terms" value={form} onChange={patch => setForm(f => ({ ...f, ...patch }))}
              result={createPv} loading={createPreview.loading} error={createPreview.error}
              idleText="Select supplier, RM, quantity and rate on every line to calculate GST." />
          </form>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setDialogOpen(false)}
              className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">Cancel</button>
            <button type="submit" form="po-form" data-testid="po-save"
              disabled={!canSave || saving}
              className="h-10 px-5 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40">
              <CheckCircleIcon size={14} weight="bold" />
              {saving ? "Saving…" : "Save as Draft"}
            </button>
            {canSubmitDoc("PO") && (<button type="button" data-testid="po-submit-authorisation"
              onClick={() => submit(null, true)}
              disabled={!canSave || saving}
              className="h-10 px-5 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-40">
              {saving ? "Submitting…" : "Submit for Authorisation"}
            </button>)}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="po-detail" description="Detailed Purchase Order view with supplier, line items, GRN receipts and approval history.">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-3 flex-wrap">
                  <ClipboardTextIcon size={20} weight="bold" className="text-[var(--accent)]" />
                  <span className="font-mono">{detail.po_no}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${badgeCls(detail.status)}`}>{detail.status}</span>
                  <span data-testid="po-detail-auth-badge" className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${authBadgeCls(authState(detail))}`}>{authState(detail)}</span>
                  <GstControlBadge po={detail} />
                  {revisionLabel(detail) && (
                    <span data-testid="po-detail-revision" className="inline-flex items-center px-2 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest">{revisionLabel(detail)}</span>
                  )}
                  {amendState(detail) && (
                    <span data-testid="po-detail-amendment-badge" className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${amendState(detail).cls}`}>{amendState(detail).label}</span>
                  )}
                  {amendPendingLabel(detail) && (
                    <span
                      data-testid="po-detail-amend-pending-badge"
                      className="inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest border-amber-500/50 bg-amber-500/10 text-amber-300"
                      title="An Amendment against this PO is awaiting Management Authorisation. The effective PO remains Authorised at the current revision until the Amendment is Authorised."
                    >
                      {amendPendingLabel(detail)}
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mt-2">
                <div><div className="overline mb-0.5">Supplier</div><div className="text-white">{detail.supplier_name}</div></div>
                <div><div className="overline mb-0.5">PO Date</div><div className="font-mono">{fmtDateIST(detail.po_date)}</div></div>
                <div><div className="overline mb-0.5">Expected</div><div className="font-mono">{(detail.expected_date || "").slice(0, 10) || "—"}</div></div>
                <div><div className="overline mb-0.5">{gstPo ? "Goods Value (excl. GST)" : "Total Value"}</div><div className="font-mono" data-testid="po-detail-total-value">₹{Number(detail.total_value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div></div>
                {gstPo && (
                  <div><div className="overline mb-0.5">Grand Total (incl. GST)</div><div className="font-mono text-white" data-testid="po-detail-grand-total">{savedResult(detail)?.totals?.grand_total == null ? "Unresolved" : `₹${Number(savedResult(detail).totals.grand_total).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</div></div>
                )}
                {(detail.source_pr_no || detail.source_mrp_no || detail.source_sales_order_no || detail.source_work_order_no) && (
                  <>
                    <div>
                      <div className="overline mb-0.5">Source PR · MRP</div>
                      <div className="font-mono text-[11px]">
                        <span className="text-[var(--accent)]">{detail.source_pr_no || "—"}</span>
                        {detail.source_mrp_no && <span className="text-zinc-500"> · {detail.source_mrp_no}</span>}
                      </div>
                    </div>
                    <div>
                      <div className="overline mb-0.5">Sales Order</div>
                      <div className="font-mono text-[11px] flex items-center gap-1.5" data-testid="po-detail-so-ref">
                        {detail.source_sales_order_no ? (
                          <>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full border uppercase ${(detail.source_sales_order_no || "").startsWith("ESO") ? "bg-blue-500/15 text-blue-300 border-blue-500/40" : "bg-purple-500/15 text-purple-300 border-purple-500/40"}`}>{(detail.source_sales_order_no || "").startsWith("ESO") ? "ESO" : "SO"}</span>
                            <span className="text-zinc-200">{detail.source_sales_order_no}</span>
                          </>
                        ) : <span className="text-zinc-600">—</span>}
                      </div>
                    </div>
                    <div>
                      <div className="overline mb-0.5">Work Order</div>
                      <div className="font-mono text-[11px] text-zinc-200" data-testid="po-detail-wo-ref">{detail.source_work_order_no || <span className="text-zinc-600">—</span>}</div>
                    </div>
                  </>
                )}
              </div>

              {/* PO-2B2-UAT-D2 · Feb 2026 — Management Rejection callout.
                  Rendered ONLY when the active amendment has a stored
                  rejection audit (rejected_at populated). Read-only.
                  Rendered verbatim from `amendments[i]` — never
                  fabricated. Effective PO stays AUTHORISED (Style-Y);
                  this callout only surfaces the amendment-level audit
                  so the operator knows what to fix before re-submit. */}
              {(() => {
                const amd = activeAmendment(detail);
                if (!amd || amd.state !== "Draft" || !amd.rejected_at) return null;
                return (
                  <div data-testid="po-detail-rejection-callout" className="border border-rose-500/40 bg-rose-500/5 rounded-sm p-3 mt-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-rose-300">Management Rejection · {amd.amendment_no}</span>
                      <span className="text-[10px] font-mono text-zinc-500">Correct & Resubmit</span>
                    </div>
                    <div className="text-xs text-zinc-200 mb-1"><span className="text-zinc-500 mr-1">Comments:</span>{amd.rejection_comments || "—"}</div>
                    <div className="text-[10px] font-mono text-zinc-500">Rejected by <span className="text-white">{amd.rejected_by || "—"}</span> · <span>{(amd.rejected_at || "").slice(0, 19).replace("T", " ")}</span></div>
                  </div>
                );
              })()}

              {/* PO-2B2-UAT-D4 · Feb 2026 — Active Proposed Amendment banner.
                  Rendered whenever the PO carries an active Draft or
                  Pending Re-authorisation amendment. Read-only. Never
                  implies proposed values are effective — Style-Y is
                  preserved (live po.lines untouched until Management
                  Authorisation flips revision). */}
              {(() => {
                const amd = activeAmendment(detail);
                if (!amd || editMode) return null;
                const isDraft = amd.state === "Draft";
                return (
                  <div data-testid="po-detail-amend-banner"
                       className={`border rounded-sm p-3 mt-3 ${isDraft ? "border-amber-500/40 bg-amber-500/5" : "border-cyan-500/40 bg-cyan-500/5"}`}>
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className={`text-[10px] font-mono uppercase tracking-widest ${isDraft ? "text-amber-300" : "text-cyan-300"}`}>
                        Proposed Amendment · {amd.amendment_no} · {isDraft ? "Draft" : "Pending Re-authorisation"}
                      </span>
                      <span className="text-[10px] font-mono text-zinc-500">Effective PO remains Authorised at Revision {Number(detail.revision || 0)} until Management Authorisation</span>
                    </div>
                    <div className="text-xs text-zinc-200"><span className="text-zinc-500 mr-1">Reason:</span>{amd.reason || "—"}</div>
                  </div>
                );
              })()}

              <div className="border border-[var(--border)] rounded-sm overflow-x-auto mt-3">
                <table className="w-full text-sm table-fixed">
                  <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
                    <tr>
                      <th className="px-2 py-1.5 text-left w-8">#</th>
                      <th className="px-2 py-1.5 text-left">RM Code</th>
                      <th className="px-2 py-1.5 text-right w-24">Required</th>
                      <th className="px-2 py-1.5 text-right w-20">Adjust</th>
                      <th className="px-2 py-1.5 text-right w-24">Total</th>
                      {/* PO-2B2-UAT-D4 · Proposed Total rendered only when an active
                          Draft/Pending amendment exists AND not in edit mode.
                          PO-GST-01 FIX01 · the unapproved "Prop. Buffer" column was removed. */}
                      {(!editMode && activeAmendment(detail)) && (
                        <th className="px-2 py-1.5 text-right w-24 bg-amber-500/10 text-amber-300" title="Proposed Total (Required + proposed Adjust) · not effective until Management Authorisation">Prop. Total</th>
                      )}
                      <th className="px-2 py-1.5 text-right w-20">Received</th>
                      <th className="px-2 py-1.5 text-right w-20" data-testid="po-detail-pending-header" title={editMode ? "Saved pending quantity — unsaved edits are not reflected" : undefined}>{editMode ? "Saved Pending" : "Pending"}</th>
                      <th className="px-2 py-1.5 text-right w-20">Rate</th>
                      {gstPo && <th className="px-2 py-1.5 text-left w-24">HSN</th>}
                      {gstPo && editMode && <th className="px-2 py-1.5 text-right w-20">Disc %</th>}
                      {gstPo && editMode && <th className="px-2 py-1.5 text-right w-24">Disc ₹</th>}
                      {gstPo && <th className="px-2 py-1.5 text-right w-28">GST %</th>}
                      <th className="px-2 py-1.5 text-left w-32">Delivery</th>
                      {editMode && <th className="px-2 py-1.5 text-left min-w-[180px]">Adj Notes</th>}
                      <th className="px-2 py-1.5 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.lines || []).map((l, i) => {
                      const el = editMode ? editLines[i] : null;
                      const req = Number(l.qty_required ?? l.qty_ordered ?? 0);
                      const adj = editMode ? Number(el?.qty_adjustment ?? 0) : Number(l.qty_adjustment ?? 0);
                      const total = editMode ? (req + adj) : Number(l.qty_total ?? l.qty_ordered ?? 0);
                      // Feb 2026 · Rate stays editable until ANY receipt is
                      // posted on the PO. Was Draft-only; users needed to
                      // correct rates on approved-but-open POs after supplier
                      // negotiation.
                      const rateEditable = editMode && !(detail.lines || []).some(x => Number(x.qty_received || 0) > 0);
                      // PO-2B2-UAT-D4 · Feb 2026 — client-side Proposed
                      // projection for this row. Sourced ONLY from
                      // detail.pending_changes.lines matched by canonical
                      // line_no. Absence of the field means "no proposed
                      // change" → Proposed value MIRRORS Current (owner
                      // directive 4). Explicit qty_adjustment === 0 is a
                      // real proposed zero — never treated as absent.
                      // Formula for total mirrors _po_amend_merge_line
                      // (backend D1 canonical engine): req + adj.
                      const amdActive = !editMode ? activeAmendment(detail) : null;
                      const pl = amdActive
                        ? ((detail.pending_changes && detail.pending_changes.lines) || []).find(x => x.line_no === l.line_no)
                        : null;
                      const hasProposedAdj = !!pl && ("qty_adjustment" in pl) && pl.qty_adjustment !== null && pl.qty_adjustment !== undefined;
                      const proposedAdj = hasProposedAdj ? Number(pl.qty_adjustment) : Number(l.qty_adjustment ?? 0);
                      const proposedTotal = req + proposedAdj;
                      const adjChanged = hasProposedAdj && proposedAdj !== Number(l.qty_adjustment ?? 0);
                      return (
                        <tr key={i} className="border-t border-[var(--border)]">
                          <td className="px-2 py-1.5 text-xs font-mono text-zinc-500">{l.line_no}</td>
                          <td className="px-2 py-1.5 text-xs"><span className="font-mono text-[var(--accent)]">{l.rm_code}</span></td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-xs">{fmtQty(req)}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-xs">
                            {editMode ? (
                              <input data-testid={`po-adj-${i}`} type="number" step="0.001"
                                value={el?.qty_adjustment ?? 0}
                                onChange={e => setEditLines(prev => prev.map((x, j) => j === i ? { ...x, qty_adjustment: e.target.value } : x))}
                                className="w-20 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-right text-xs font-mono focus:outline-none focus:border-[var(--accent)]" />
                            ) : (
                              <span className={adj > 0 ? "text-amber-300" : adj < 0 ? "text-red-300" : "text-zinc-500"}>{adj !== 0 ? (adj > 0 ? "+" : "") + fmtQty(adj) : "0.000"}</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-xs text-emerald-300 font-semibold" data-testid={`po-total-${i}`}>{fmtQty(total)}</td>
                          {(!editMode && amdActive) && (
                            <td data-testid={`po-detail-proposed-total-${i}`}
                                className={`px-2 py-1.5 text-right font-mono tabular-nums text-xs bg-amber-500/5 ${adjChanged ? "text-emerald-200 font-semibold" : "text-zinc-500"}`}>
                              {fmtQty(proposedTotal)}
                            </td>
                          )}
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-xs text-emerald-300">{fmtQty(l.qty_received || 0)}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-xs text-amber-300">{fmtQty(l.qty_pending || 0)}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums text-xs">
                            {rateEditable ? (
                              <input data-testid={`po-rate-${i}`} type="number" step="0.01" min="0"
                                value={el?.unit_cost ?? 0}
                                onChange={e => setEditLines(prev => prev.map((x, j) => j === i ? { ...x, unit_cost: e.target.value } : x))}
                                className="w-20 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-right text-xs font-mono focus:outline-none focus:border-[var(--accent)]" />
                            ) : (
                              <span>₹{Number((editMode ? el?.unit_cost : l.unit_cost) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            )}
                          </td>
                          {gstPo && (
                            <td className="px-2 py-1.5 text-xs font-mono">
                              {editMode ? (
                                <input data-testid={`po-hsn-${i}`} type="text" maxLength={12} value={el?.hsn_code || ""}
                                  onChange={e => updEditLine(i, { hsn_code: e.target.value.replace(/\s+/g, "") })}
                                  className="w-20 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono focus:outline-none focus:border-[var(--accent)]" />
                              ) : (
                                <span data-testid={`po-detail-hsn-${i}`}>{l.hsn_code || "—"}{l.hsn_source ? <span className="ml-1 text-[9px] text-zinc-500 uppercase">{l.hsn_source}</span> : null}</span>
                              )}
                            </td>
                          )}
                          {gstPo && editMode && <DiscountCells line={el || {}} onChange={patch => updEditLine(i, patch)} testid={`po-edit-${i}`} />}
                          {gstPo && (
                            <td className="px-2 py-1.5 text-right text-xs font-mono">
                              {editMode ? (
                                <GstRateCell line={el || {}} onChange={patch => updEditLine(i, patch)} source={editSource(l.line_no)} testid={`po-edit-${i}`} />
                              ) : (
                                <span data-testid={`po-detail-gst-${i}`}>{l.tax_pct == null ? "—" : `${l.tax_pct}%`}{l.gst_rate_source ? <span className="ml-1 text-[9px] text-zinc-500 uppercase">{l.gst_rate_source}</span> : null}</span>
                              )}
                            </td>
                          )}
                          <td className="px-2 py-1.5 text-xs font-mono">
                            {editMode ? (
                              <input data-testid={`po-exp-${i}`} type="date"
                                value={el?.expected_date || ""}
                                onChange={e => setEditLines(prev => prev.map((x, j) => j === i ? { ...x, expected_date: e.target.value } : x))}
                                className="w-32 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono focus:outline-none focus:border-[var(--accent)]" />
                            ) : (
                              <span className="text-zinc-300">{(l.expected_date || detail.expected_date || "").slice(0, 10) || "—"}{l.expected_date && detail.expected_date && l.expected_date !== detail.expected_date ? <span className="ml-1 text-[9px] text-amber-400" title="Line-level override">*</span> : null}</span>
                            )}
                          </td>
                          {editMode && (
                            <td className="px-2 py-1.5 text-xs">
                              <input data-testid={`po-notes-${i}`} type="text" maxLength={140}
                                placeholder="e.g. MOQ 1 bag = 25 kg"
                                value={el?.adjustment_notes ?? ""}
                                onChange={e => setEditLines(prev => prev.map((x, j) => j === i ? { ...x, adjustment_notes: e.target.value } : x))}
                                className="w-full h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
                            </td>
                          )}
                          <td className="px-2 py-1.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${badgeCls(l.status)}`}>{l.status}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {gstPo && editMode && editHdr && (
                <GstTermsSection testid="po-edit-gst-terms" value={editHdr} onChange={patch => setEditHdr(h => ({ ...h, ...patch }))}
                  result={previewResult(editPreview.data)} loading={editPreview.loading} error={editPreview.error}
                  idleText="Calculating GST…" />
              )}
              {gstPo && !editMode && (
                <div data-testid="po-detail-gst-summary" className="border border-[var(--border)] rounded-sm p-3 mt-3 bg-[var(--bg-0)]/40">
                  <div className="overline mb-2">GST &amp; Terms · Saved</div>
                  <div className="text-[11px] text-zinc-400 mb-2 flex flex-wrap gap-x-4 gap-y-1 font-mono">
                    <span>Place of supply: {detail.place_of_supply_state_code || (detail.company_is_receiving_location ? "Company location" : "—")}</span>
                    <span>Reverse charge: {detail.reverse_charge ? "Yes" : "No"}</span>
                    <span>Charges: {(detail.charges || []).map(c => `${c.label || c.code} ₹${c.amount} (${c.treatment})`).join(", ") || "None"}</span>
                  </div>
                  <GstResultPanel result={savedResult(detail)} idleText="No GST snapshot on this PO." />
                </div>
              )}
              {!gstPo && editMode && (
                <div data-testid="po-edit-legacy-note" className="text-[11px] text-zinc-500 mt-3">Legacy PO · GST inputs are not available here; existing edit behaviour is unchanged and the PO is not converted.</div>
              )}
              {detail.remarks && <div className="text-xs text-zinc-400 mt-3"><span className="overline">Remarks · </span>{detail.remarks}</div>}
              {/* Edit / Print footer */}
              <div className="flex items-center justify-end gap-2 mt-4 border-t border-[var(--border)] pt-3">
                {/* Feb 2026 · Modify Order — replaces the legacy Unapprove
                    button. Opens the shared ModifyDocumentPopover which
                    link-probes GRNs / stock movements and either lets an
                    Admin Unapprove (when nothing is linked) or Short Close /
                    Cancel Balance per line (when receipts already exist).
                    PO-AUTH-EDIT-AMEND-H01 · Phase 2A · Feb 2026 — hidden
                    on Authorised POs. Direct commercial mutation of an
                    Authorised PO is no longer permitted via the buyer
                    surface; the controlled Amendment lifecycle (Phase 2B)
                    will replace this affordance post-Authorisation. */}
                {["Open", "Approved", "Partially Received"].includes(detail.status) && getUser()?.role === "Administrator" && !isAuthorisedPo(detail) && (
                  <button
                    data-testid="po-detail-modify"
                    onClick={() => setModifyOpen(true)}
                    className="h-9 px-4 border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 text-xs font-mono uppercase tracking-widest rounded-sm flex items-center gap-1.5 mr-auto"
                  >Modify Order</button>
                )}
                <button data-testid="po-detail-pdf" onClick={() => openPdf(detail)}
                  className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm flex items-center gap-1.5">
                  <PrinterIcon size={12} weight="bold" />Print PDF
                </button>
                {/* DP-PO-INITIAL-DRAFT-QUEUE · Phase 2 · Feb 2026 —
                    Fresh-Open + Rejected-Draft explicit Submit affordance.
                    Enforces `Save ≠ Submit` at the UI: fresh Open POs
                    now expose an explicit "Submit for Authorisation"
                    action; Rejected-Draft POs expose the sealed
                    "Resubmit for Authorisation" action. All other
                    lifecycle states hide the button (Pending, Authorised,
                    Cancelled, Received, or ineligible). Backend remains
                    authoritative — `submit_po_for_approval` refuses
                    with HTTP 400 on any state that slips past the UI. */}
                {/* PO-GST-01 · P2-D1-C-FIX01 · exact backend predicate; hidden until GRN history is known
                    so the historical-GRN warning can never be skipped. */}
                {!editMode && !eligibilityLoading && submitBlockedByGrn(detail) && canSubmitDoc("PO") && (
                  <span data-testid="po-detail-submit-blocked-grn" title={GRN_SUBMIT_BLOCKED}
                    className="h-9 px-4 rounded-sm border border-red-500/50 bg-red-500/10 text-red-300 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5">
                    Submit blocked · GRN posted
                  </span>
                )}
                {!editMode && !eligibilityLoading && canSubmitPo(detail) && !submitBlockedByGrn(detail) && canSubmitDoc("PO") && (
                  <button data-testid="po-detail-submit-approval"
                    onClick={async () => {
                      if (!window.confirm(submitConfirmText(detail))) return;
                      try {
                        await api.post(`/purchase-orders/${detail.id}/submit-for-approval`);
                        toast.success(`${detail.po_no} · sent for Management Authorisation`);
                        setDetail(null); load();
                      } catch (e) {
                        toast.error(apiErrorMessage(e, "Submit failed"));
                      }
                    }}
                    className="h-9 px-4 rounded-sm bg-amber-500 hover:bg-amber-600 text-black text-xs font-semibold uppercase tracking-widest flex items-center gap-1.5">
                    {isRejectedDraft(detail) ? "Resubmit for Authorisation" : "Submit for Authorisation"}
                  </button>
                )}
                {!editMode && detail.status === "Pending Admin Approval" && (
                  <span data-testid="po-locked-pending-auth" className="h-9 px-4 rounded-sm border border-amber-500/50 bg-amber-500/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5">
                    Pending Management Authorisation · Read Only
                  </span>
                )}
                {!editMode && eligibilityLoading && (
                  <span data-testid="po-detail-eligibility-checking" className="h-9 px-4 rounded-sm border border-zinc-500/40 bg-zinc-500/10 text-zinc-300 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5">
                    Checking edit eligibility…
                  </span>
                )}
                {!editMode && detail.has_posted_grn_history === true && (
                  <span data-testid="po-detail-grn-locked" className="h-9 px-4 rounded-sm border border-rose-500/40 bg-rose-500/10 text-rose-200 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5">
                    {GRN_LOCK_TEXT}
                  </span>
                )}
                {!editMode && !grnLocked && detail.status !== "Received" && detail.status !== "Cancelled" && detail.status !== "Pending Admin Approval" && !isAuthorisedPo(detail) && (
                  <button data-testid="po-detail-edit" onClick={startEdit}
                    className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm flex items-center gap-1.5">
                    <PencilSimpleIcon size={12} weight="bold" />Edit Adjustments
                  </button>
                )}
                {/* PO-AUTH-EDIT-AMEND-H01 · Phase 2A/2B · Feb 2026 —
                    on Authorised POs render the amendment affordances
                    in place of Modify / Edit. Direct commercial edits
                    are permanently locked; the controlled Amendment
                    lifecycle is the canonical path. Draft amendments
                    reopen (never create a new AMD number); Pending
                    amendments show a read-only status hint. */}
                {!editMode && isAuthorisedPo(detail) && detail.status !== "Received" && detail.status !== "Cancelled" && (
                  <>
                    {grnLocked ? (
                      // J2 · GRN history → no commercial amendment actions; a historical Draft may only be cancelled.
                      detail.has_posted_grn_history === true && activeAmendment(detail)?.state === "Draft" ? (
                        <button data-testid="po-detail-amend-cancel-draft" onClick={cancelDraftAmendment}
                          className="h-9 px-4 rounded-sm border border-zinc-500/50 hover:bg-[var(--bg-2)] text-zinc-200 text-xs font-mono uppercase tracking-widest flex items-center gap-1.5">
                          <XIcon size={12} weight="bold" /> Cancel Draft Amendment · {activeAmendment(detail).amendment_no}
                        </button>
                      ) : activeAmendment(detail)?.state === "Pending Re-authorisation" ? (
                        <span data-testid="po-detail-amend-pending" className="h-9 px-4 rounded-sm border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5">
                          {activeAmendment(detail).amendment_no} · Pending Re-authorisation
                        </span>
                      ) : null
                    ) : activeAmendment(detail) && activeAmendment(detail).state === "Draft" ? (
                      <button data-testid="po-detail-amend-continue" onClick={openAmendment}
                        className="h-9 px-4 rounded-sm border border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 text-xs font-mono uppercase tracking-widest flex items-center gap-1.5">
                        <PencilSimpleIcon size={12} weight="bold" /> Continue Amendment · {activeAmendment(detail).amendment_no}
                      </button>
                    ) : activeAmendment(detail) && activeAmendment(detail).state === "Pending Re-authorisation" ? (
                      <span data-testid="po-detail-amend-pending" className="h-9 px-4 rounded-sm border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5">
                        {activeAmendment(detail).amendment_no} · Pending Re-authorisation
                      </span>
                    ) : (
                      <button data-testid="po-detail-amend" onClick={openAmendment}
                        className="h-9 px-4 rounded-sm border border-[var(--accent)]/60 bg-[var(--accent)]/10 text-[var(--accent)] hover:bg-[var(--accent)]/20 text-xs font-mono uppercase tracking-widest flex items-center gap-1.5">
                        <PencilSimpleIcon size={12} weight="bold" /> Amend Order
                      </button>
                    )}
                    <span data-testid="po-detail-locked" className="h-9 px-4 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5">
                      Authorised · Locked
                    </span>
                  </>
                )}
                {/* Amendment History — read-only, visible whenever the
                    PO carries ≥ 1 historical amendment entry (Draft,
                    Pending, Authorised or terminal Rejected). Renders
                    directly from `amendments[]` (canonical audit array). */}
                {!editMode && Array.isArray(detail.amendments) && detail.amendments.length > 0 && (
                  <button data-testid="po-detail-amend-history" onClick={() => setAmendHistoryOpen(true)}
                    className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm flex items-center gap-1.5">
                    <ArrowCounterClockwiseIcon size={12} weight="bold" /> Amendment History
                  </button>
                )}
                {editMode && (
                  <>
                    <button className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm" onClick={cancelEdit} disabled={savingEdit}>Cancel</button>
                    <button data-testid="po-detail-save" className="h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-xs font-mono uppercase tracking-widest rounded-sm flex items-center gap-1.5" onClick={saveEdit} disabled={savingEdit}>
                      <CheckCircleIcon size={12} weight="bold" />{savingEdit ? "Saving…" : "Save Adjustments"}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Feb 2026 · Dynamic Order Modification popover for POs */}
      {detail && (
        <ModifyDocumentPopover
          docType="PO"
          docId={detail.id}
          docNo={detail.po_no}
          open={modifyOpen}
          onClose={() => setModifyOpen(false)}
          onDone={async () => { setDetail(null); await load(); }}
          onLineCancel={async () => { await load(); }}
          testidPrefix="po-modify"
        />
      )}
      <CancelWithReasonDialog
        open={!!cancelTarget}
        onOpenChange={(o) => !o && setCancelTarget(null)}
        docType="Purchase Order"
        docNo={cancelTarget?.po_no}
        cancelPath={cancelTarget ? `/purchase-orders/${cancelTarget.id}/cancel` : ""}
        onDone={load}
      />
      {/* PO-AUTH-EDIT-AMEND-H01 · Phase 2B.2 · Amendment editor —
          drives `pending_changes` only via the canonical Phase 2B.1
          backend routes. Never mutates live PO fields. */}
      {detail && (
        <AmendmentEditor
          open={amendOpen}
          onOpenChange={setAmendOpen}
          po={detail}
          onSaved={async () => { await refreshDetail(); await load(); }}
          onSubmitted={async () => { setAmendOpen(false); await refreshDetail(); await load(); }}
          onCancelDraft={cancelDraftAmendment}
        />
      )}
      {detail && (
        <CancelAmendmentDialog open={cancelAmdOpen} onOpenChange={setCancelAmdOpen} po={detail}
          amd={activeAmendment(detail)} onDone={afterAmendCancel} />
      )}
      {/* PO-AUTH-EDIT-AMEND-H01 · Phase 2B.2 · Amendment history —
          read-only, sourced verbatim from `amendments[]`. */}
      {detail && (
        <AmendmentHistoryDialog
          open={amendHistoryOpen}
          onOpenChange={setAmendHistoryOpen}
          po={detail}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PO-AUTH-EDIT-AMEND-H01 · Phase 2B.2 · Amendment editor component
// ─────────────────────────────────────────────────────────────────
// Renders CURRENT AUTHORISED vs PROPOSED AMENDMENT columns for header
// + line whitelist (owner directive C). Mandatory Amendment Reason
// (owner directive D). Submit routes through the canonical Phase 2B.1
// backend endpoint (owner directive F) — never edits live PO fields.
// Never allows creation of a second active amendment (owner directive E).
function AmendmentEditor({ open, onOpenChange, po, onSaved, onSubmitted, onCancelDraft }) {
  const amd = po ? (po.amendments || []).find(a => a.amendment_no === po.active_amendment_ref) : null;
  const pending = po?.pending_changes || null;
  const [reason, setReason] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [lineEdits, setLineEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // PO-GST-01 · P2-D1-D · GST-controlled amendment inputs (saved pending → fallback live PO).
  const [gstHdr, setGstHdr] = useState(null);
  const [gstLines, setGstLines] = useState({});
  const gstPo = isGstControlled(po);

  useEffect(() => {
    if (!open || !po || !amd) return;
    const ph = pending?.header || {};
    setReason(pending?.reason || amd.reason || "");
    setExpectedDate(ph.expected_date != null ? ph.expected_date : (po.expected_date || "").slice(0, 10));
    setRemarks(ph.remarks != null ? ph.remarks : (po.remarks || ""));
    const seed = {};
    (pending?.lines || []).forEach(pl => { seed[pl.line_no] = { ...pl }; });
    setLineEdits(seed);
    const g = initAmendGst(po, pending);
    setGstHdr(g.header); setGstLines(g.lines);
  }, [open, po?.id, amd?.amendment_no]); // eslint-disable-line react-hooks/exhaustive-deps

  const grnLocked = po?.has_posted_grn_history !== false;
  const editable = !!amd && (amd.state || "Draft") === "Draft" && !grnLocked;
  const buildBody = () => buildAmendBody({ po, pending, reason, expectedDate, remarks, lineEdits, gstPo, gstHdr, gstLines });
  const previewBody = () => { const { amendment_reason, ...rest } = buildBody(); return rest; }; // eslint-disable-line no-unused-vars
  const amendPreview = useGstPreview(open && gstPo && editable && gstHdr ? { po_id: po.id, amend: previewBody() } : null,
                                     open && gstPo && editable && !!gstHdr);

  if (!po || !amd) return null;
  const state = amd.state || "Draft";
  const readOnly = !editable;
  const setGstLine = (ln, patch) => setGstLines(prev => ({ ...prev, [ln]: { ...(prev[ln] || {}), ...patch } }));
  const previewSource = (ln) => {
    const li = (amendPreview.data?.line_inputs || []).find(x => x.line_no === ln);
    if (!li) return "";
    return li.tax_pct == null ? "GST unresolved" : `${li.tax_pct}% · ${li.gst_rate_source || "—"}`;
  };

  const liveLines = po.lines || [];
  const setLine = (ln, patch) => setLineEdits(prev => ({
    ...prev,
    [ln]: { line_no: ln, ...(prev[ln] || {}), ...patch },
  }));

  const savePending = async () => {
    if ((reason || "").trim().length < 4) { toast.error("Amendment Reason is required (min 4 characters)"); return; }
    setSaving(true);
    try {
      await api.put(`/purchase-orders/${po.id}/amend/pending`, buildBody());
      toast.success(`${amd.amendment_no} draft saved`);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const submitForAuth = async () => {
    if ((reason || "").trim().length < 4) { toast.error("Amendment Reason is required (min 4 characters)"); return; }
    setSubmitting(true);
    try {
      // Persist latest edits first so the backend sees the current diff.
      await api.put(`/purchase-orders/${po.id}/amend/pending`, buildBody());
      await api.post(`/purchase-orders/${po.id}/amend/submit`);
      toast.success(`${amd.amendment_no} · Submitted for Re-authorisation`);
      if (onSubmitted) await onSubmitted();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Submit failed"));
    } finally { setSubmitting(false); }
  };

  const fmtCell = (v) => v === "" || v == null ? <span className="text-zinc-600">—</span> : String(v);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-6xl max-h-[92vh] overflow-y-auto"
        data-testid="po-amend-editor"
        description="Amendment editor — edits pending_changes only, never live PO fields.">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3 flex-wrap">
            <span>Amend Purchase Order · {po.po_no}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest">Revision {Number(po.revision || 0)} · Currently Effective</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${state === "Draft" ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-cyan-500/15 text-cyan-300 border-cyan-500/40"}`}>{amd.amendment_no} · {state === "Draft" ? "DRAFT" : "PENDING RE-AUTHORISATION"}</span>
          </DialogTitle>
        </DialogHeader>

        <div data-testid="po-amend-banner" className="text-[11px] font-mono text-zinc-400 leading-relaxed border border-[var(--border)] bg-[var(--bg-0)]/50 rounded-sm px-3 py-2">
          {gstPo ? (
            <>The effective PO remains AUTHORISED at Revision {Number(po.revision || 0)}. This amendment edits <span className="text-emerald-300">pending_changes only</span> until Management re-authorises it as Revision {Number(po.revision || 0) + 1}. On this GST-controlled PO you may propose HSN, GST rate (incl. confirmed 0%), discounts, charges, place of supply, receiving location, reverse charge and delivery dates; the server recalculates GST authoritatively on Save Draft, Submit and activation. Supplier, RM identity, allocation, source references, required qty and UOM are not editable.</>
          ) : (
            <>The effective PO remains AUTHORISED at Revision {Number(po.revision || 0)}. This amendment edits <span className="text-emerald-300">pending_changes only</span> and never modifies live commercial values until Management authorises it. H1-protected fields (supplier, RM identity, allocation, source references, HSN, tax rate, required qty, UOM) are not editable through the amendment surface.</>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2"><GstControlBadge po={po} /></div>
        {grnLocked && state === "Draft" && (
          <div data-testid="po-amend-grn-lock" className="mt-2 text-xs text-amber-200 border border-amber-500/40 bg-amber-500/10 rounded-sm px-3 py-2">
            {po.has_posted_grn_history === true ? `${GRN_LOCK_TEXT} Save Draft and Submit are unavailable; the Draft may only be cancelled.` : "Checking edit eligibility…"}
          </div>
        )}

        {/* PO-2B2-UAT-D2.1 · Feb 2026 — Management Rejection callout
            inside the Amendment Editor. Shown when the active Draft
            amendment has a rejection audit. Verbatim from canonical
            `amendments[i]` — never fabricated. Guides the operator to
            correct proposed values before resubmit. */}
        {amd.rejected_at ? (
          <div data-testid="po-amend-editor-rejection" className="border border-rose-500/40 bg-rose-500/5 rounded-sm p-3 mt-3">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-mono uppercase tracking-widest text-rose-300">Management Rejection · {amd.amendment_no}</span>
              <span className="text-[10px] font-mono text-zinc-500">Correct proposed values below and resubmit</span>
            </div>
            <div className="text-xs text-zinc-200 mb-1"><span className="text-zinc-500 mr-1">Comments:</span>{amd.rejection_comments || "—"}</div>
            <div className="text-[10px] font-mono text-zinc-500">Rejected by <span className="text-white">{amd.rejected_by || "—"}</span> · <span>{(amd.rejected_at || "").slice(0, 19).replace("T", " ")}</span></div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          {/* CURRENT AUTHORISED · header */}
          <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-0)]/40">
            <div className="overline mb-2 text-emerald-400">Current Authorised</div>
            <div className="space-y-1.5 text-xs">
              <div className="flex gap-3"><span className="text-zinc-500 w-32">Expected Date</span><span className="font-mono">{(po.expected_date || "").slice(0, 10) || "—"}</span></div>
              <div className="flex gap-3"><span className="text-zinc-500 w-32">Remarks</span><span className="font-mono">{po.remarks || "—"}</span></div>
              <div className="flex gap-3"><span className="text-zinc-500 w-32">{gstPo ? "Goods Value (excl. GST)" : "Total Value"}</span><span className="font-mono">₹{Number(po.total_value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
            </div>
          </div>
          {/* PROPOSED · header */}
          <div className="border border-[var(--accent)]/40 rounded-sm p-3 bg-[var(--accent)]/5">
            <div className="overline mb-2 text-[var(--accent)]">Proposed Amendment</div>
            <div className="space-y-1.5 text-xs">
              <label className="flex gap-3 items-center"><span className="text-zinc-500 w-32">Expected Date</span>
                <input type="date" data-testid="po-amend-expected" value={expectedDate} disabled={readOnly}
                  onChange={e => setExpectedDate(e.target.value)}
                  className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono focus:outline-none focus:border-[var(--accent)] disabled:opacity-50" /></label>
              <label className="flex gap-3 items-start"><span className="text-zinc-500 w-32 pt-1">Remarks</span>
                <input type="text" data-testid="po-amend-remarks" value={remarks} disabled={readOnly}
                  onChange={e => setRemarks(e.target.value)}
                  className="flex-1 h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)] disabled:opacity-50" /></label>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <label className="overline text-[10px] mb-1 block text-amber-300">Amendment Reason * <span className="text-zinc-500 normal-case tracking-normal">— required, minimum 4 characters</span></label>
          <textarea rows={2} data-testid="po-amend-reason" value={reason} disabled={readOnly}
            onChange={e => setReason(e.target.value)} placeholder="Why is this amendment being raised? (supplier rate revision, extend delivery, …)"
            className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] disabled:opacity-50" />
        </div>

        {gstPo && gstHdr && (
          <div className="mt-3 border border-[var(--border)] rounded-sm p-3 space-y-3" data-testid="po-amend-gst-terms">
            <div className="overline text-[10px]">Proposed GST &amp; Terms</div>
            <fieldset disabled={readOnly} className="space-y-3 disabled:opacity-60">
              <GstHeaderFields value={gstHdr} onChange={patch => setGstHdr(h => ({ ...h, ...patch }))} />
              <ChargesEditor charges={gstHdr.charges} onChange={charges => setGstHdr(h => ({ ...h, charges }))} />
            </fieldset>
          </div>
        )}
        {gstPo && <AmendGstPanels po={po} amd={amd} preview={amendPreview} editable={editable} />}

        {/* Line-level table */}
        <div className="overline mt-3 text-[10px]">Line-level Proposed Changes</div>
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-2 py-2 text-left w-10">#</th>
                <th className="px-2 py-2 text-left">RM</th>
                <th className="px-2 py-2 text-right w-20">Req.</th>
                <th className="px-2 py-2 text-right w-24" title="Current Authorised Adjustment">Adj (Curr)</th>
                <th className="px-2 py-2 text-right w-24" title="Proposed Adjustment">Adj (Prop)</th>
                <th className="px-2 py-2 text-right w-24" title="Current Rate ₹">Rate (Curr)</th>
                <th className="px-2 py-2 text-right w-24" title="Proposed Rate ₹">Rate (Prop)</th>
                <th className="px-2 py-2 text-left min-w-[180px]">Adj Notes (Prop)</th>
                {gstPo && <th className="px-2 py-2 text-left w-24">HSN (Prop)</th>}
                {gstPo && <th className="px-2 py-2 text-right w-28">GST % (Prop)</th>}
                {gstPo && <th className="px-2 py-2 text-right w-16">Disc %</th>}
                {gstPo && <th className="px-2 py-2 text-right w-20">Disc ₹</th>}
                {gstPo && <th className="px-2 py-2 text-left w-32">Delivery (Prop)</th>}
              </tr>
            </thead>
            <tbody>
              {liveLines.map((l) => {
                const edit = lineEdits[l.line_no] || {};
                const curAdj = Number(l.qty_adjustment ?? 0);
                const curRate = Number(l.unit_cost ?? 0);
                return (
                  <tr key={l.line_no} className="border-t border-[var(--border)]">
                    <td className="px-2 py-1.5 text-xs font-mono text-zinc-500">{l.line_no}</td>
                    <td className="px-2 py-1.5"><span className="font-mono text-[var(--accent)]">{l.rm_code}</span><div className="text-[10px] text-zinc-500">{l.rm_name}</div></td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtQty(Number(l.qty_required ?? 0))}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-zinc-400">{fmtQty(curAdj)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <input type="number" step="0.001" data-testid={`po-amend-adj-${l.line_no}`}
                        value={edit.qty_adjustment ?? curAdj} disabled={readOnly}
                        onChange={e => setLine(l.line_no, { qty_adjustment: e.target.value })}
                        placeholder={curAdj > 0 ? "clear = 0" : ""}
                        title={curAdj > 0 ? "Clear this field to remove the buffer (qty_adjustment → 0)" : ""}
                        className="w-24 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-right text-xs font-mono focus:outline-none focus:border-[var(--accent)] disabled:opacity-50" />
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-zinc-400">₹{curRate.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-2 py-1.5 text-right">
                      <input type="number" step="0.01" data-testid={`po-amend-rate-${l.line_no}`}
                        value={edit.unit_cost ?? curRate} disabled={readOnly}
                        onChange={e => setLine(l.line_no, { unit_cost: e.target.value })}
                        className="w-24 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-right text-xs font-mono focus:outline-none focus:border-[var(--accent)] disabled:opacity-50" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" maxLength={140} data-testid={`po-amend-notes-${l.line_no}`}
                        value={edit.adjustment_notes ?? (l.adjustment_notes || "")} disabled={readOnly}
                        onChange={e => setLine(l.line_no, { adjustment_notes: e.target.value })}
                        placeholder="e.g. supplier email dated 12-Feb"
                        className="w-full h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)] disabled:opacity-50" />
                    </td>
                    {gstPo && (() => {
                      const gl = gstLines[l.line_no] || {};
                      const tid = `po-amend-${l.line_no}`;
                      return (
                        <>
                          <td className="px-2 py-1.5">
                            <input data-testid={`${tid}-hsn`} type="text" maxLength={12} value={gl.hsn_code || ""} disabled={readOnly}
                              onChange={e => setGstLine(l.line_no, { hsn_code: e.target.value.replace(/\s+/g, "") })}
                              className="w-20 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono focus:outline-none focus:border-[var(--accent)] disabled:opacity-50" />
                            <div className="text-[9px] text-zinc-600 font-mono">curr {l.hsn_code || "—"}</div>
                          </td>
                          <td className="px-2 py-1.5">
                            <GstRateCell line={gl} onChange={patch => setGstLine(l.line_no, patch)} source={previewSource(l.line_no)} testid={tid} disabled={readOnly} />
                            <div className="text-[9px] text-zinc-600 font-mono text-right">curr {l.tax_pct == null ? "—" : `${l.tax_pct}%`}</div>
                          </td>
                          <DiscountCells line={gl} onChange={patch => setGstLine(l.line_no, patch)} testid={tid} disabled={readOnly} />
                          <td className="px-2 py-1.5">
                            <input data-testid={`${tid}-delivery`} type="date" value={gl.expected_date || ""} disabled={readOnly}
                              onChange={e => setGstLine(l.line_no, { expected_date: e.target.value })}
                              className="h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono focus:outline-none focus:border-[var(--accent)] disabled:opacity-50" />
                            <div className="text-[9px] text-zinc-600 font-mono">curr {(l.expected_date || "").slice(0, 10) || "—"}</div>
                          </td>
                        </>
                      );
                    })()}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Diff summary chips (compact) */}
        {(amd.diff && (amd.diff.header?.length || amd.diff.lines?.length)) ? (
          <div className="mt-3 border border-[var(--border)] rounded-sm bg-[var(--bg-0)]/40 p-3" data-testid="po-amend-diff">
            <div className="overline text-[10px] mb-2">Saved Draft Diff</div>
            {(amd.diff.header || []).map((d, i) => (
              <div key={`h-${i}`} className="text-[11px] font-mono flex gap-2 py-0.5">
                <span className="text-zinc-500 w-32">{d.field}</span>
                <span className="text-rose-300">{fmtCell(d.prev)}</span>
                <span className="text-zinc-500">→</span>
                <span className="text-emerald-300">{fmtCell(d.new)}</span>
              </div>
            ))}
            {(amd.diff.lines || []).map((d, i) => (
              <div key={`l-${i}`} className="text-[11px] font-mono flex gap-2 py-0.5">
                <span className="text-zinc-500 w-32">Line {d.line_no} · {d.field}</span>
                <span className="text-rose-300">{fmtCell(d.prev)}</span>
                <span className="text-zinc-500">→</span>
                <span className="text-emerald-300">{fmtCell(d.new)}</span>
              </div>
            ))}
          </div>
        ) : null}

        <DialogFooter className="gap-2 mt-4">
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm" data-testid="po-amend-close">Close</button>
          {state === "Draft" && onCancelDraft && (
            <button onClick={onCancelDraft} data-testid="po-amend-cancel-draft"
              className="h-9 px-4 border border-rose-500/40 text-rose-200 hover:bg-rose-500/10 text-xs font-mono uppercase tracking-widest rounded-sm flex items-center gap-1.5">
              <XIcon size={12} weight="bold" />Cancel Draft
            </button>
          )}
          {!readOnly && (
            <>
              <button onClick={savePending} disabled={saving || submitting}
                data-testid="po-amend-save-draft"
                className="h-9 px-4 border border-amber-500/50 text-amber-200 hover:bg-amber-500/10 text-xs font-mono uppercase tracking-widest rounded-sm flex items-center gap-1.5 disabled:opacity-40">
                {saving ? "Saving…" : "Save Draft"}
              </button>
              {canSubmitDoc("PO") && (<button onClick={submitForAuth} disabled={saving || submitting}
                data-testid="po-amend-submit"
                className="h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-xs font-mono uppercase tracking-widest rounded-sm flex items-center gap-1.5 disabled:opacity-40">
                <CheckCircleIcon size={12} weight="bold" />{submitting ? "Submitting…" : "Submit for Re-authorisation"}
              </button>)}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────
// PO-AUTH-EDIT-AMEND-H01 · Phase 2B.2 · Amendment history dialog
// ─────────────────────────────────────────────────────────────────
// Read-only. Renders verbatim from `amendments[]` — never synthesises
// history that is not actually stored (owner directive K).
function AmendmentHistoryDialog({ open, onOpenChange, po }) {
  if (!po) return null;
  const entries = Array.isArray(po.amendments) ? [...po.amendments] : [];
  const stateCls = (s) => ({
    "Draft": "bg-amber-500/15 text-amber-300 border-amber-500/40",
    "Pending Re-authorisation": "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
    "Authorised": "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    "Rejected": "bg-rose-500/15 text-rose-300 border-rose-500/40",
    "Cancelled": "bg-zinc-500/20 text-zinc-300 border-zinc-400/50",
  }[s] || "bg-zinc-500/15 text-zinc-400 border-zinc-500/30");
  const fmtV = (v) => v === "" || v == null ? "—" : String(v);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-5xl max-h-[92vh] overflow-y-auto"
        data-testid="po-amend-history"
        description="Read-only history of every amendment raised against this PO. Rendered verbatim from the canonical amendments[] audit trail.">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <ArrowCounterClockwiseIcon size={18} weight="bold" className="text-[var(--accent)]" />
            <span>Amendment History · {po.po_no}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest">Revision {Number(po.revision || 0)}</span>
          </DialogTitle>
        </DialogHeader>
        {entries.length === 0 && (<div className="text-xs text-zinc-500 py-6 text-center">No amendments recorded on this PO yet.</div>)}
        <div className="space-y-3 mt-2">
          {entries.map((a, i) => (
            <div key={i} data-testid={`po-amend-history-entry-${a.amendment_no || i}`}
              className="border border-[var(--border)] rounded-sm bg-[var(--bg-0)]/40 p-3">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <span className="font-mono text-sm text-white">{a.amendment_no || `#${i + 1}`}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${stateCls(a.state)}`}>{a.state}</span>
                <span className="text-[10px] font-mono text-zinc-500">Base Revision <span className="text-zinc-300">{Number(a.base_revision ?? 0)}</span></span>
                {a.resulting_revision != null && (
                  <span className="text-[10px] font-mono text-zinc-500">Resulting Revision <span className="text-emerald-300">{a.resulting_revision}</span></span>
                )}
              </div>
              <div className="text-xs text-zinc-300 mb-2"><span className="text-zinc-500 mr-1">Reason:</span>{a.reason || "—"}</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] font-mono text-zinc-400">
                <div><div className="text-zinc-500">Created</div><div>{a.created_by || "—"}<div className="text-[9px] text-zinc-600">{(a.created_at || "").slice(0, 19).replace("T", " ")}</div></div></div>
                <div><div className="text-zinc-500">Last Edited</div><div>{a.last_edit_by || "—"}<div className="text-[9px] text-zinc-600">{(a.last_edit_at || "").slice(0, 19).replace("T", " ")}</div></div></div>
                <div><div className="text-zinc-500">Submitted</div><div>{a.submitted_by || "—"}<div className="text-[9px] text-zinc-600">{(a.submitted_at || "").slice(0, 19).replace("T", " ")}</div></div></div>
                <div><div className="text-zinc-500">Authorised</div><div>{a.authorised_by || "—"}<div className="text-[9px] text-zinc-600">{(a.authorised_at || "").slice(0, 19).replace("T", " ")}</div></div></div>
                {a.rejected_by ? (
                  <div className="col-span-2 md:col-span-4"><div className="text-rose-400">Rejected · {a.rejected_by}<span className="text-zinc-600 ml-2">{(a.rejected_at || "").slice(0, 19).replace("T", " ")}</span></div><div className="text-rose-300 text-[10px] font-normal">Comments: {a.rejection_comments || "—"}</div></div>
                ) : null}
              </div>
              {/* Diff */}
              {a.diff && ((a.diff.header || []).length + (a.diff.lines || []).length > 0) ? (
                <div className="border-t border-[var(--border)] mt-2 pt-2">
                  <div className="overline text-[10px] mb-1">Field-level Diff</div>
                  {(a.diff.header || []).map((d, k) => (
                    <div key={k} className="text-[11px] font-mono flex gap-2">
                      <span className="text-zinc-500 w-32">{d.field}</span>
                      <span className="text-rose-300">{fmtV(d.prev)}</span>
                      <span className="text-zinc-500">→</span>
                      <span className="text-emerald-300">{fmtV(d.new)}</span>
                    </div>
                  ))}
                  {(a.diff.lines || []).map((d, k) => (
                    <div key={`ln-${k}`} className="text-[11px] font-mono flex gap-2">
                      <span className="text-zinc-500 w-32">Line {d.line_no} · {d.field}</span>
                      <span className="text-rose-300">{fmtV(d.prev)}</span>
                      <span className="text-zinc-500">→</span>
                      <span className="text-emerald-300">{fmtV(d.new)}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              <AmendmentHistoryGst a={a} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm" data-testid="po-amend-history-close">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
